import { Auth } from '@aws-amplify/auth';
import fileDownload from 'js-file-download';
import { ActionTakenLabelOverrides, getCustomActionTakenLabel } from './action-taken-labels';
import axios, { AxiosInstance } from 'axios';
import _ from 'lodash';
import * as pako from 'pako';
import {
  ActionTakenType,
  DesignActionTakenType,
  DocumentPriorityType,
  DocumentTemplateType,
  EmailStatus,
  FileCategoryType,
  FileNode,
  IAddress,
  IBid,
  IBidSetup,
  IDocument,
  IDocumentTemplate,
  IFile,
  INumberedDocumentView,
  IProductPackage,
  IProjectUser,
  IProjectView,
  ISecurityGroup,
  ISimplifiedProjectView,
  IUser,
  IUserGroup,
  PunchListStatusType,
  SecurityPermissionLevel,
  WorkflowStatusType,
} from '../api-client/autogenerated';
import {
  getBluebeamXML,
  getFileById,
  getFileUrlById,
  getFileUrlByIdForConsultant,
} from '../models/api/files';
import {
  addSpacing,
  ascendingComparator,
  descendingComparator,
  getPayApplicationStage,
  statusToGeneralString,
} from '../main-components/document-index/DocumentIndexUtils';
import path from 'path';
import dayjs from 'dayjs';
import { v4 } from 'uuid';
import {
  getUsersFromUserGroup,
  ManagePermissionsDialogType,
} from '../main-components/design/ManagePermissionsDialog';
import { hasPermissionToView } from './store-utils';
import { Project } from '../features/project/project';
import axiosRateLimit from 'axios-rate-limit';
import { getTemplateIds } from '../models/api/templates';
import { getDocumentsByProjectId } from '../models/api/project';
import { ExpandedJobStatusType } from '../main-components/document-conforming-center/DCCUtils';
import QRCode from 'easyqrcodejs';
import qrcodeLogo from '../images/centerline-logo-new.png';
import { FileWithPath } from 'react-dropzone';
import { verifyFileByUrl } from '../models/api/filesystem';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';

export type SeverityType = 'error' | 'success' | 'info' | 'warning' | undefined;

export enum PayApplicationStage {
  DRAFT_FOR_REVIEW = 'Draft for Review',
  FINAL_FOR_APPROVAL = 'Final for Approval',
  FINAL_FOR_RECORD_ONLY = 'Final for Record Only',
}

/**
 * Workflow statuses for which a final pay application's dollar value counts
 * toward the cumulative totals in the document log and the Contract Value card.
 */
export const PAY_APPLICATION_COUNTED_STATUSES: string[] = [
  WorkflowStatusType.ReviewComplete,
  WorkflowStatusType.Completed,
  WorkflowStatusType.Resolved,
];

export const DOCUMENT_TYPES_DESIGN = [
  DocumentTemplateType.Task,
  DocumentTemplateType.DesignPackages,
  DocumentTemplateType.DesignMeetingMinutes,
];
export const DOCUMENT_TYPES_BIDDING = [
  DocumentTemplateType.BidDrawings,
  DocumentTemplateType.ProjectManual,
  DocumentTemplateType.Addenda,
  DocumentTemplateType.InformationalItems,
  DocumentTemplateType.PlanholderList,
  DocumentTemplateType.BidderRfIs,
  DocumentTemplateType.SubstitutionRequests,
];
export const DOCUMENT_TYPES_CONSTRUCTION = [
  DocumentTemplateType.Drawings,
  DocumentTemplateType.Specifications,
  DocumentTemplateType.AsiDocuments,
  DocumentTemplateType.Submittals,
  DocumentTemplateType.SubmittalPackages,
  DocumentTemplateType.RequestsForInformation,
  DocumentTemplateType.ChangeOrders,
  DocumentTemplateType.ConstructionChangeDirectives,
  DocumentTemplateType.PotentialChangeOrders,
  DocumentTemplateType.RequestsForChange,
  DocumentTemplateType.WorkChangeProposalRequests,
  DocumentTemplateType.PayApplications,
  DocumentTemplateType.FieldReports,
  DocumentTemplateType.MeetingMinutes,
  DocumentTemplateType.ContractorDailyLogs,
  DocumentTemplateType.MiscellaneousDocuments,
  DocumentTemplateType.Schedules,
  DocumentTemplateType.Testing,
  DocumentTemplateType.CaTask,
];
export const DOCUMENT_TYPES_CLOSE_OUT = [
  DocumentTemplateType.PunchList,
  DocumentTemplateType.AsBuilt,
  DocumentTemplateType.AsBuiltPackages,
  DocumentTemplateType.CloseoutSubmittals,
  DocumentTemplateType.CloseoutSubmittalPackages,
  DocumentTemplateType.OperationsAndMaintenanceData,
  DocumentTemplateType.WarrantyItems,
  DocumentTemplateType.ClearLp,
  DocumentTemplateType.CertificateOfSubstantialCompletion,
];

const MAX_REQUESTS_COUNT = 5;
const INTERVAL_MS = 10;
let PENDING_REQUESTS = 0;

export const getRateLimitedAxiosClient = (
  axiosInstance?: AxiosInstance,
  perSecond: number = 20,
) => {
  if (!axiosInstance) {
    axiosInstance = axios.create();
  }
  const api = axiosRateLimit(axiosInstance, {
    maxRPS: perSecond,
  });

  api.interceptors.request.use(function (config) {
    return new Promise((resolve, reject) => {
      let interval = setInterval(() => {
        if (PENDING_REQUESTS < MAX_REQUESTS_COUNT) {
          PENDING_REQUESTS++;
          clearInterval(interval);
          resolve(config);
        }
      }, INTERVAL_MS);
    });
  });

  api.interceptors.response.use(
    function (response) {
      PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
      return Promise.resolve(response);
    },
    function (error) {
      PENDING_REQUESTS = Math.max(0, PENDING_REQUESTS - 1);
      return Promise.reject(error);
    },
  );
  return api;
};

export const getAxiosClient = () => {
  return getRateLimitedAxiosClient();
};

export const combineClasses = (...args: string[]) => {
  let classes = '';
  if (args.length > 0) {
    classes = args.join(' ');
  }
  return classes;
};

export const getDrawingAndSpecificationFiles = (project: IProjectView | null) => {
  if (!project) return { drawingFiles: [], specificationFiles: [] };
  const files = (project.files || [])
    .sort((f1, f2) => {
      if (f1.createdOn! < f2.createdOn!) return -1;
      if (f1.createdOn === f2.createdOn) return 0;
      return 1;
    })
    .reverse();
  return {
    drawingFiles: files.filter((f) => f.category === FileCategoryType.Drawings),
    specificationFiles: files.filter((f) => f.category === FileCategoryType.Specifications),
  };
};

export const getBidDrawingAndSpecificationFiles = (bid: IBidSetup) => {
  const files = (bid.files || [])
    .sort((f1, f2) => {
      if (f1.createdOn! < f2.createdOn!) return -1;
      if (f1.createdOn === f2.createdOn) return 0;
      return 1;
    })
    .reverse();
  return {
    drawingFiles: files.filter((f) => f.category === FileCategoryType.BidDrawings),
    specificationFiles: files.filter((f) => f.category === FileCategoryType.BidSpecifications),
  };
};

export const documentIsOverdue = (
  document: Pick<
    INumberedDocumentView,
    'documentTemplate' | 'workflowStatus' | 'anticipatedInitialSubmissionDate' | 'dueDate'
  >,
  isSubmittal = false,
) => {
  const initialStatuses = [
    WorkflowStatusType.Initial,
    WorkflowStatusType.SubcontractorUploaded,
    WorkflowStatusType.ReadyForSubmissionToArchitect,
    WorkflowStatusType.GeneralContractorUploaded,
  ];
  const completeStatuses = [
    WorkflowStatusType.ReviewComplete,
    WorkflowStatusType.Completed,
    WorkflowStatusType.Resolved,
    WorkflowStatusType.Resubmitted,
  ];
  if (
    (isSubmittal ||
      document.documentTemplate?.name === DocumentTemplateType.Submittals ||
      document.documentTemplate?.name === DocumentTemplateType.CloseoutSubmittals) &&
    document.workflowStatus &&
    initialStatuses.includes(document.workflowStatus)
  )
    return (
      !!document.anticipatedInitialSubmissionDate &&
      dayjs().add(-1, 'day').isAfter(parseDate(document.anticipatedInitialSubmissionDate))
    );
  return (
    (!document.workflowStatus || !completeStatuses.includes(document.workflowStatus)) &&
    !!document.dueDate &&
    dayjs().add(-1, 'day').isAfter(parseDate(document.dueDate))
  );
};

export const documentIsRevision = (document: IDocument) => {
  return (document.revisionNumber || 0) > 0;
};

export const formatFileSize = (x: number) => {
  const xInKilos = x / 1024;
  if (xInKilos > 1024) {
    const xInMegs = xInKilos / 1024;
    return xInMegs.toFixed(1).toString().concat(' MB');
  }
  return Math.round(xInKilos).toString().concat(' KB');
};

export const formatDate = (dateStr?: string | null, omitTime = false) => {
  if (!dateStr) return 'N/A';
  // Read the date parts off the dayjs object itself rather than converting to a native Date:
  // parseDate puts UTC-midnight-anchored values (i.e. plain DATE columns, no time component)
  // into UTC mode so they show the same calendar day everywhere regardless of the browser's
  // time zone -- .toDate() followed by the local-time Date getters below used to throw that
  // away and re-introduce the same day-off-by-one bug parseDate exists to prevent.
  const dateJs = parseDate(dateStr);
  const year = dateJs.year();

  let month = (1 + dateJs.month()).toString();
  month = month.length > 1 ? month : `0${month}`;

  let day = dateJs.date().toString();
  day = day.length > 1 ? day : `0${day}`;

  if (month.toLowerCase() === 'nan' || day.toLowerCase() === 'nan') return 'N/A';
  if (omitTime) return `${month}/${day}/${year}`;

  const time = dateJs.toDate().toLocaleTimeString([], {
    // @ts-ignore
    timeStyle: 'short',
  });
  return `${month}/${day}/${year} ${time}`;
};

export const formatDateUpcoming = (dateStr: string) => {
  // Read the date parts off the dayjs object itself -- see formatDate above for why converting
  // to a native Date and using its local-time getters reintroduces the day-off-by-one bug that
  // parseDate's UTC-mode handling is meant to prevent for UTC-midnight-anchored (date-only) values.
  const date = parseDate(dateStr);

  const month = (1 + date.month()).toString();
  let monthString = '';
  switch (month) {
    case '1':
      monthString = 'Jan.';
      break;
    case '2':
      monthString = 'Feb.';
      break;
    case '3':
      monthString = 'Mar.';
      break;
    case '4':
      monthString = 'Apr.';
      break;
    case '5':
      monthString = 'May';
      break;
    case '6':
      monthString = 'June';
      break;
    case '7':
      monthString = 'July';
      break;
    case '8':
      monthString = 'Aug.';
      break;
    case '9':
      monthString = 'Sept.';
      break;
    case '10':
      monthString = 'Oct.';
      break;
    case '11':
      monthString = 'Nov.';
      break;
    case '12':
      monthString = 'Dec.';
      break;
    default:
      break;
  }

  const day = date.date().toString();
  return `${day}\n${monthString}`;
};

export const formatPhoneNumber = (str: string) => {
  // Filter only numbers from the input
  const cleaned = `${str}`.replace(/\D/g, '');

  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  return null;
};

export const formatDays = (days?: number | null): string => {
  if (days == null) return `TBD`;
  const daysInt = Math.floor(days || 0);
  if (daysInt === 1) {
    return `${daysInt} day`;
  }
  return `${daysInt} days`;
};

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatMoney = (num?: number | null): string => {
  if (num == null) return '';
  if (Number.isNaN(num)) {
    return '';
  }

  return currencyFormatter.format(num / 100);
};

/**
 * A project's location on one line -- street address, city, state -- leaving out the parts that
 * are not set. Only the state is required of a project, and a project whose location was carried
 * over from its bid setup has no street address at all, so every part but the state is optional.
 */
export const formatProjectLocation = (address?: IAddress | null): string => {
  if (!address) return '';
  return [address.address1, address.city, address.stateOrProvince?.name]
    .map((part) => part?.trim())
    .filter((part) => !!part)
    .join(', ');
};

/**
 * TRANSITIONAL (project location backfill). The location a bid setup shows belongs to its project,
 * but the code that reads it goes live before the migration that carries every existing bid
 * setup's address over -- `run_migrations` runs after `build_and_deploy`, and prod has no
 * migrations-only workflow -- so between the two the projects have no address and BidConnect would
 * show nothing. Falling back to the bid setup's own address covers that window, and any stage the
 * migration has not reached yet. Reads only: the forms write to the project alone.
 *
 * Remove this, and the `?? bid_setup.address_id` in ProjectBidServiceSetups.authorizedGetBidSetups
 * and ProjectService.getManagementArchiveObjects, once
 * 20260910120000_backfill_project_address_from_bid_setup has run on dev, staging and prod.
 */
export const formatBidSetupLocation = (bidSetup?: IBidSetup | null): string =>
  formatProjectLocation(bidSetup?.project?.address ?? bidSetup?.address);

export const addDays = (date: Date | string, days: number) => {
  const result = dayjs(date).toDate();
  result.setDate(result.getDate() + days);
  return result;
};

export const formatDateLong = (dateStr: string) => {
  const date = parseDate(dateStr).toDate();
  const dateElements = date.toDateString().split(' ');
  const month = date.toLocaleString('default', { month: 'long' });
  const dayName = date.toLocaleString('default', { weekday: 'long' });
  const time = date.toLocaleTimeString([], {
    // @ts-ignore
    timeStyle: 'short',
  });
  // The submission deadline is Tuesday on December 15, 2020 at 4:00 AM.
  return `${dayName} on ${month} ${dateElements[2]}, ${dateElements[3]} at ${time}.`;
};

export const toggleState = (boolState: boolean, setBoolState: (state: boolean) => void) => {
  setBoolState(!boolState);
};

export const setupAmplify = () => {
  Auth.configure({
    region: 'us-east-1',
    userPoolId: process.env.REACT_APP_COGNITO_USERPOOL_ID || 'us-east-1_yT7PBd3hs',
    userPoolWebClientId: process.env.REACT_APP_COGNITO_WEBCLIENT_ID || '1bunfc5fejade7mtji04jqpj8v',
    authenticationFlowType: 'USER_PASSWORD_AUTH',
  });
};

// TODO: make sure we're actually writing the write data and passing url/name to the file before opening
export const openInNewTab = (fileId: string, bidding?: boolean, query?: string) => {
  const newWindow = window.open(
    `${window.location.origin}/${bidding ? 'bidding' : 'main'}/${
      isPublicPage() ? 'public/' : ''
    }pdf/${fileId}${query ? `?query=${encodeURIComponent(query)}` : ''}`,
    '_blank',
    'noopener,noreferrer',
  );
  if (newWindow) newWindow.opener = null;
};

export const openInNewWindow = (fileId: string, bidding?: boolean) => {
  const newWindow = window.open(
    `${window.location.origin}/${bidding ? 'bidding' : 'main'}/${
      isPublicPage() ? 'public/' : ''
    }pdf/${fileId}`,
    '_blank',
    `location=yes,height=${window.innerHeight * 0.9},width=840,scrollbars=yes,status=yes`,
  );
  if (newWindow) newWindow.opener = null;
};

export const fileIsPdf = (file: IFile) => {
  return file.url?.toLowerCase().endsWith('.pdf');
};

export const fileIsImage = (file: IFile) => {
  return (
    file.url?.toLowerCase().endsWith('.jpg') ||
    file.url?.toLowerCase().endsWith('.jpeg') ||
    file.url?.toLowerCase().endsWith('.png')
  );
};

export const openFilesystemFileInNewTab = (
  projectId: string,
  fullName: string,
  fullKey: string,
  ownerId: string,
  isRecycleBin?: boolean,
  query?: string,
) => {
  if (isRecycleBin) {
    localStorage.setItem('s3Key', fullKey);
  } else {
    localStorage.setItem('projectId', projectId);
    localStorage.setItem('fullKey', fullKey);
    localStorage.setItem('ownerId', ownerId);
  }
  localStorage.setItem('fullName', fullName);

  const searchParams = new URLSearchParams();
  if (query) {
    searchParams.set('query', query);
  }

  const newWindow = window.open(
    `${window.location.origin}/main/pdf/design${query ? `?${searchParams.toString()}` : ''}`,
    '_blank',
    'noopener,noreferrer',
  );
  if (newWindow) newWindow.opener = null;
};

export const openPublicFilesystemFileInNewTab = (
  projectId: string,
  fullName: string,
  fullKey: string,
  designFileShareId: string,
  publicAccessKey: string,
) => {
  localStorage.setItem('projectId', projectId);
  localStorage.setItem('fullName', fullName);
  localStorage.setItem('designFileShareId', designFileShareId);
  localStorage.setItem('fullKey', fullKey);
  localStorage.setItem('publicAccessKey', publicAccessKey);
  const newWindow = window.open(
    `${window.location.origin}/main/pdf/design`,
    '_blank',
    'noopener,noreferrer',
  );
  if (newWindow) newWindow.opener = null;
};

export const openInBluebeam = (
  fileId: string,
  filename = 'bluebeam',
  additionalReviewDocumentId?: string,
) => {
  return getBluebeamXML(fileId, additionalReviewDocumentId)
    .then((xml) => {
      fileDownload(xml, `${path.basename(filename, '.pdf')}.bfx`, 'application/bfx');
    })
    .catch((e) => console.error(e));
};

export const getFileBlob = async (
  fileId: string,
  fullSize?: boolean,
  onDownloadProgress?: (progressEvent: any) => void,
): Promise<Blob | undefined> => {
  const { downloadLink } = await getFileUrlById(fileId, fullSize);
  if (downloadLink) {
    const { data } = await getAxiosClient().get<Blob>(downloadLink, {
      responseType: 'blob',
      onDownloadProgress,
    });
    return data;
  }
  return undefined;
};

export const downloadFileById = async (
  fileId: string,
  filename: string,
  fileType?: string,
  fullSize = true,
) => {
  const { downloadLink } = await getFileUrlById(fileId, fullSize, filename);
  if (downloadLink) {
    downloadFileFromLink(downloadLink, filename);
  }
};

export const downloadFileFromLink = (link: string, filename: string) => {
  const anchor = document.createElement('a');
  anchor.href = link;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const openFileInNewTab = async (fileId: string) => {
  const { downloadLink } = await getFileUrlById(fileId, true);
  if (!downloadLink) return;
  const newWindow = window.open(downloadLink, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

export const downloadFileAsConsultant = async (
  fileId: string,
  filename: string,
  reviewDocumentId: string,
  fileType?: string,
) => {
  const { downloadLink } = await getFileUrlByIdForConsultant(fileId, reviewDocumentId);
  if (downloadLink) {
    downloadFileFromLink(downloadLink, filename);
  }
};

export const getImageByFileId = async (fileId: string, fullSize?: boolean) => {
  const response = await getFileUrlById(fileId, fullSize);
  const { data } = await getAxiosClient().get<Blob>(response.downloadLink, {
    responseType: 'blob',
  });
  return URL.createObjectURL(data);
};

export const documentTypeLookup: Record<string, string> = {
  'Const. Change Directives': DocumentTemplateType.ConstructionChangeDirectives,
  'Work Change Proposal Req.': DocumentTemplateType.WorkChangeProposalRequests,
  'Substantial Completion Cert.': DocumentTemplateType.CertificateOfSubstantialCompletion,
  'O&M Data': DocumentTemplateType.OperationsAndMaintenanceData,
  'As-Built Drawings': DocumentTemplateType.AsBuilt,
  'Bid Submissions': DocumentTemplateType.BidTabulation,
  'Design Task Manager': DocumentTemplateType.Task,
  'CA Task Manager': DocumentTemplateType.CaTask,
  'CA Meeting Minutes': DocumentTemplateType.MeetingMinutes,
  'Design Package Review': DocumentTemplateType.DesignPackages,
  'Design Meeting Minutes': DocumentTemplateType.DesignMeetingMinutes,
};

export const documentTypeToReadable: Record<DocumentTemplateType, string> = {
  [DocumentTemplateType.Addenda]: 'Addendum',
  [DocumentTemplateType.AdditionalReview]: 'Consultant Review',
  [DocumentTemplateType.AsBuilt]: 'As-Built',
  [DocumentTemplateType.AsBuiltPackages]: 'As-Built Packages',
  [DocumentTemplateType.AsiDocuments]: 'ASI',
  [DocumentTemplateType.BidAdvertisement]: 'Bid Advertisement',
  [DocumentTemplateType.BidderRfIs]: 'Bidder RFI',
  [DocumentTemplateType.BidDrawings]: 'Bid Drawing',
  [DocumentTemplateType.BidTabulation]: 'Bid Tabulation',
  [DocumentTemplateType.CertificateOfSubstantialCompletion]: 'Substantial Completion Cert.',
  [DocumentTemplateType.ChangeOrders]: 'Change Order',
  [DocumentTemplateType.ClearLp]: 'Clear L&P',
  [DocumentTemplateType.CloseoutSubmittals]: 'Closeout Submittal',
  [DocumentTemplateType.ConstructionChangeDirectives]: 'Const. Change. Directive',
  [DocumentTemplateType.ContractorDailyLogs]: 'Daily Log',
  [DocumentTemplateType.Drawings]: 'Drawings',
  [DocumentTemplateType.FieldReports]: 'Field Report',
  [DocumentTemplateType.InformationalItems]: 'Info Item',
  [DocumentTemplateType.MeetingMinutes]: 'CA Meeting Minutes',
  [DocumentTemplateType.DesignMeetingMinutes]: 'Design Meeting Minutes',
  [DocumentTemplateType.MiscellaneousDocuments]: 'Misc Document',
  [DocumentTemplateType.OperationsAndMaintenanceData]: 'O&M',
  [DocumentTemplateType.PayApplications]: 'Pay Application',
  [DocumentTemplateType.PlanholderList]: 'Planholder List',
  [DocumentTemplateType.PotentialChangeOrders]: 'PCO',
  [DocumentTemplateType.SubstitutionRequests]: 'Substitution Request',
  [DocumentTemplateType.ProjectManual]: 'Project Manual',
  [DocumentTemplateType.PunchList]: 'Punch List',
  [DocumentTemplateType.RegulatoryApprovals]: 'Regulatory Approval',
  [DocumentTemplateType.RequestsForChange]: 'RFC',
  [DocumentTemplateType.RequestsForInformation]: 'RFI',
  [DocumentTemplateType.Schedules]: 'Schedule',
  [DocumentTemplateType.Specifications]: 'Specification',
  [DocumentTemplateType.SubmittalPackages]: 'Submittal Package',
  [DocumentTemplateType.CloseoutSubmittalPackages]: 'Closeout Submittal Package',
  [DocumentTemplateType.Submittals]: 'Submittal',
  [DocumentTemplateType.Task]: 'Design Task',
  [DocumentTemplateType.CaTask]: 'CA Task',
  [DocumentTemplateType.DesignPackages]: 'Design Package',
  [DocumentTemplateType.Testing]: 'Testing',
  [DocumentTemplateType.WarrantyItems]: 'Warranty Item',
  [DocumentTemplateType.WorkChangeProposalRequests]: 'WCPR',
};

export const documentTypeToUrl: Record<DocumentTemplateType, string> = {
  [DocumentTemplateType.Addenda]: 'addenda',
  [DocumentTemplateType.AdditionalReview]: 'additional-review',
  [DocumentTemplateType.AsBuilt]: 'as-built',
  [DocumentTemplateType.AsBuiltPackages]: 'as-built-packages',
  [DocumentTemplateType.AsiDocuments]: 'asi-docs',
  [DocumentTemplateType.BidAdvertisement]: 'bid-advertisement',
  [DocumentTemplateType.BidderRfIs]: 'bidder-rfis',
  [DocumentTemplateType.BidDrawings]: 'bid-drawings',
  [DocumentTemplateType.BidTabulation]: 'bid-submissions',
  [DocumentTemplateType.CertificateOfSubstantialCompletion]: 'substantial-completion-cert',
  [DocumentTemplateType.ChangeOrders]: 'change-orders',
  [DocumentTemplateType.ClearLp]: 'clear-lp',
  [DocumentTemplateType.CloseoutSubmittals]: 'closeout-submittals',
  [DocumentTemplateType.ConstructionChangeDirectives]: 'const-change-directives',
  [DocumentTemplateType.ContractorDailyLogs]: 'daily-logs',
  [DocumentTemplateType.Drawings]: 'drawings',
  [DocumentTemplateType.FieldReports]: 'field-reports',
  [DocumentTemplateType.InformationalItems]: 'info-items',
  [DocumentTemplateType.MeetingMinutes]: 'meeting-minutes',
  [DocumentTemplateType.DesignMeetingMinutes]: 'design-meeting-minutes',
  [DocumentTemplateType.MiscellaneousDocuments]: 'misc-docs',
  [DocumentTemplateType.OperationsAndMaintenanceData]: 'om-data',
  [DocumentTemplateType.PayApplications]: 'pay-applications',
  [DocumentTemplateType.PlanholderList]: 'planholder-list',
  [DocumentTemplateType.PotentialChangeOrders]: 'potential-change-orders',
  [DocumentTemplateType.SubstitutionRequests]: 'substitution-requests',
  [DocumentTemplateType.ProjectManual]: 'project-manual',
  [DocumentTemplateType.PunchList]: 'punch-list',
  [DocumentTemplateType.RegulatoryApprovals]: 'regulatory-approvals',
  [DocumentTemplateType.RequestsForChange]: 'rfc',
  [DocumentTemplateType.RequestsForInformation]: 'rfi',
  [DocumentTemplateType.Schedules]: 'schedules',
  [DocumentTemplateType.Specifications]: 'specifications',
  [DocumentTemplateType.SubmittalPackages]: 'submittals',
  [DocumentTemplateType.CloseoutSubmittalPackages]: 'closeout-packages',
  [DocumentTemplateType.Submittals]: 'submittals',
  [DocumentTemplateType.Task]: 'tasks',
  [DocumentTemplateType.CaTask]: 'ca-tasks',
  [DocumentTemplateType.DesignPackages]: 'design-packages',
  [DocumentTemplateType.Testing]: 'testing',
  [DocumentTemplateType.WarrantyItems]: 'warranty-items',
  [DocumentTemplateType.WorkChangeProposalRequests]: 'work-change-prop-requests',
};

export enum SubscriberAccessType {
  DESIGN = 'Design',
  BIDDING = 'Bidding',
  CONSTRUCTION_CLOSEOUT = 'Construction/Closeout',
}

export const documentTypeToAccessType: Record<
  DocumentTemplateType,
  SubscriberAccessType | undefined
> = {
  [DocumentTemplateType.Addenda]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.AdditionalReview]: undefined,
  [DocumentTemplateType.AsBuilt]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.AsBuiltPackages]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.AsiDocuments]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.BidAdvertisement]: undefined,
  [DocumentTemplateType.BidderRfIs]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.BidDrawings]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.BidTabulation]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.CertificateOfSubstantialCompletion]:
    SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.ChangeOrders]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.ClearLp]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.CloseoutSubmittals]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.ConstructionChangeDirectives]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.ContractorDailyLogs]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.Drawings]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.FieldReports]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.InformationalItems]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.MeetingMinutes]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.DesignMeetingMinutes]: SubscriberAccessType.DESIGN,
  [DocumentTemplateType.MiscellaneousDocuments]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.OperationsAndMaintenanceData]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.PayApplications]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.PlanholderList]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.PotentialChangeOrders]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.SubstitutionRequests]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.ProjectManual]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.PunchList]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.RegulatoryApprovals]: SubscriberAccessType.BIDDING,
  [DocumentTemplateType.RequestsForChange]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.RequestsForInformation]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.Schedules]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.Specifications]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.SubmittalPackages]: undefined,
  [DocumentTemplateType.CloseoutSubmittalPackages]: undefined,
  [DocumentTemplateType.Submittals]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.Task]: SubscriberAccessType.DESIGN,
  [DocumentTemplateType.CaTask]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.DesignPackages]: SubscriberAccessType.DESIGN,
  [DocumentTemplateType.Testing]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.WarrantyItems]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
  [DocumentTemplateType.WorkChangeProposalRequests]: SubscriberAccessType.CONSTRUCTION_CLOSEOUT,
};

export const userCanViewDocument = (
  url: string,
  canViewDesign: boolean,
  canViewBidding: boolean,
  canViewConstructionAndCloseout: boolean,
): boolean => {
  const splitUrl = url.split('/');
  if (splitUrl.includes('design')) {
    return canViewDesign;
  }
  const index = splitUrl.findIndex((x) => x === 'documents');
  const documentType: string = urlToDocumentType[splitUrl[index + 1]];
  const accessType: SubscriberAccessType | undefined =
    documentTypeToAccessType[documentType as DocumentTemplateType];
  if (accessType === SubscriberAccessType.BIDDING) {
    return canViewBidding;
  } else if (accessType === SubscriberAccessType.CONSTRUCTION_CLOSEOUT) {
    return canViewConstructionAndCloseout;
  } else if (accessType === SubscriberAccessType.DESIGN) {
    return canViewDesign;
  } else {
    return false;
  }
};

/**
 * Document types with no create or edit path of their own. Drawings and specifications arrive
 * through the Publishing Center, bid submissions through their own upload, and the rest are
 * published rather than authored, so none of them offers a CREATE button.
 */
export const docTypesNoCreateOrEdit = [
  DocumentTemplateType.BidDrawings,
  DocumentTemplateType.ProjectManual,
  DocumentTemplateType.BidTabulation,
  DocumentTemplateType.PlanholderList,
  DocumentTemplateType.SubstitutionRequests,
  DocumentTemplateType.BidderRfIs,
  DocumentTemplateType.Addenda,
  DocumentTemplateType.InformationalItems,
  DocumentTemplateType.Specifications,
  DocumentTemplateType.Drawings,
];

/**
 * This user's permission level on one document type.
 *
 * store-utils' getDocumentTypePermission answers the same question only for the log page currently
 * open (it reads state.documents.type), so anything asking about an arbitrary type -- the nav
 * drawer, the log toolbar, the dashboard -- has had to inline this lookup. By convention level 2 is
 * create/edit and level 3 is elevated.
 */
export const getPermissionForDocumentType = (
  type: DocumentTemplateType,
  security: ISecurityGroup | null | undefined,
  documentTemplateNameToId: Record<string, string>,
  user?: IUser | null,
  currentProject?: Project | null,
): SecurityPermissionLevel => {
  if (user?.isSiteAdmin) return SecurityPermissionLevel.NUMBER_4;
  if (user?.adminOfSubscriberId && user.adminOfSubscriberId === currentProject?.subscriberId) {
    return SecurityPermissionLevel.NUMBER_4;
  }
  const documentTemplateId = documentTemplateNameToId[type];
  if (!documentTemplateId) return SecurityPermissionLevel.NUMBER_0;
  return (
    security?.securityGroupDocumentTemplateList?.find(
      (group) => group.documentTemplateId === documentTemplateId,
    )?.securityPermissionLevel ?? SecurityPermissionLevel.NUMBER_0
  );
};

export const urlToDocumentType = _.invert(documentTypeToUrl);
export const urlToDocumentTypeReadable = (url: string) =>
  documentTypeToReadable[urlToDocumentType[url] as DocumentTemplateType];

export type PdfMatchParams = { fileId: string };
export type ProjectMatchParams = { projectId: string };
export type OptionalProjectMatchParams = Partial<ProjectMatchParams>;
export type IndexMatchParams = { projectId: string; type: string };
export type DocumentMatchParams = {
  projectId: string;
  type: string;
  documentId: string;
  commentId?: string;
};
export type DocumentOrLocationMatchParams = {
  projectId: string;
  type: string;
  documentId?: string;
  locationId?: string;
};
export type PublicDocumentMatchParams = {
  key: string;
};
export type RemindersMatchParams = { key?: string };
export type DCCMatchParams = { fileId?: string };
export type PublicDesignMatchParams = {
  projectId: string;
  designFileShareId: string;
  publicAccessKey: string;
};

export const uuidPattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;

export const showDocumentDisplay = (type: string) => {
  const lookup = urlToDocumentType[type];
  return (
    lookup === DocumentTemplateType.Submittals ||
    lookup === DocumentTemplateType.Specifications ||
    lookup === DocumentTemplateType.CloseoutSubmittals
  );
};

/**
 * The two bidding-period documents an outside bidder files and the architect answers: Bidder RFIs
 * and Substitution Requests.
 *
 * They run the same review workflow as a Construction RFI minus its contractor-response step --
 * the bidder submits, the architect reviews, and the review completes the document -- so a fair
 * amount of RFI branching applies to them with the contractor half taken out.
 */
export const isBidderReviewDocumentType = (docType?: DocumentTemplateType | null): boolean =>
  !!docType &&
  [DocumentTemplateType.BidderRfIs, DocumentTemplateType.SubstitutionRequests].includes(docType);

/**
 * The Substitution Request form the bidder fills in on the bidding portal, in the order it asks
 * for them. Description is deliberately not one of these: the portal captures it whether the
 * bidder fills the form in or uploads a completed one, so it cannot tell the two apart.
 */
export const SUBSTITUTION_REQUEST_FORM_FIELDS = [
  'submittalSection',
  'pageNumber',
  'paragraphNumber',
  'proposedSubstitution',
  'substitutionReason',
  'substitutionAffectsDrawing',
  'substitutionAffectsTrades',
  'agency',
  'substitutionDifferences',
  'manufacturersWarranties',
  'manufacturersWarrantiesExplanation',
  'informationAvailable',
] as const;

/** Whether the bidder filled the form in rather than uploading a completed one. */
export const hasSubstitutionRequestForm = (document?: INumberedDocumentView | null): boolean =>
  !!document &&
  SUBSTITUTION_REQUEST_FORM_FIELDS.some((field) => {
    const value = document[field];
    return value !== null && value !== undefined && value !== '';
  });

export const getUserFacingDocumentStatus = (
  doc: INumberedDocumentView | null,
  docType?: DocumentTemplateType,
  labelOverrides?: ActionTakenLabelOverrides,
) => {
  if (!doc) return '';

  const documentType = (docType || doc.documentTemplate?.name) as DocumentTemplateType;

  // Checked before the priority branch, and separately from it: a custom label applies when "For
  // Information Only" is the ACTION TAKEN, but never when the same words come from
  // DocumentPriorityType (task priority), which is a different enum the subscriber cannot rename.
  if (doc.actionTaken === ActionTakenType.ForInformationOnly) {
    return getActionTakenText(ActionTakenType.ForInformationOnly, labelOverrides, documentType);
  }

  if (doc.priority === DocumentPriorityType.ForInformationOnly) {
    return 'For Information Only';
  }

  if (doc.actionTaken === ActionTakenType.Withdrawn) {
    return 'Withdrawn';
  }

  if (doc.actionTaken === ActionTakenType.Rejected) {
    return getActionTakenText(ActionTakenType.Rejected, labelOverrides, documentType);
  }

  if (
    documentType === DocumentTemplateType.PunchList ||
    documentType === DocumentTemplateType.WarrantyItems
  )
    return statusToGeneralString[doc.punchListStatus as PunchListStatusType];

  const { workflowStatus } = doc as IDocument;

  if (
    documentType === DocumentTemplateType.AsBuilt ||
    documentType === DocumentTemplateType.AsBuiltPackages
  )
    return getAsBuiltStatus(doc);

  if (
    documentType === DocumentTemplateType.Task ||
    documentType === DocumentTemplateType.CaTask ||
    documentType === DocumentTemplateType.DesignPackages
  ) {
    return getUserFriendlyTaskStatusFromWorkflowStatus(workflowStatus);
  }

  return getUserFacingWorkflowStatus(workflowStatus);
};

export const getUserFacingWorkflowStatus = (status: WorkflowStatusType | null | undefined) => {
  switch (status) {
    case WorkflowStatusType.Initial:
    case WorkflowStatusType.SubcontractorUploaded:
    case WorkflowStatusType.ReadyForSubmissionToArchitect:
    case WorkflowStatusType.GeneralContractorUploaded:
      return 'Awaiting Submission';
    case WorkflowStatusType.SubmittedForReview:
      return 'Submitted for Review';
    case WorkflowStatusType.UnderReview:
    case WorkflowStatusType.ArchitectUploaded:
      return 'Under Review';
    case WorkflowStatusType.ReviewComplete:
    case WorkflowStatusType.Completed:
    case WorkflowStatusType.Resubmitted:
    case WorkflowStatusType.Accepted:
      return 'Review Complete';
    case WorkflowStatusType.Resolved:
      return 'Resolved';
    case WorkflowStatusType.Canceled:
      return 'Canceled';
    default:
      return 'N/A';
  }
};

/** The shipped wording for each action, before any subscriber customization. */
export const BUILT_IN_ACTION_TAKEN_TEXT: Record<ActionTakenType, string> = {
  [ActionTakenType.AmendAsNoted]: 'Amend as Noted',
  [ActionTakenType.NoExceptionsTaken]: 'No Exceptions Taken',
  [ActionTakenType.ReviseAndResubmit]: 'Revise & Resubmit',
  [ActionTakenType.SeeSubmittalComments]: 'See Submittal Comments',
  [ActionTakenType.SeeTransmittalComments]: 'See Transmittal Comments',
  [ActionTakenType.SubmitSpecifiedItem]: 'Submit Specified Item',
  [ActionTakenType.Withdrawn]: 'Withdrawn',
  [ActionTakenType.ForInformationOnly]: 'For Information Only',
  [ActionTakenType.RemoveFromPackage]: 'Remove from Package',
  [ActionTakenType.Rejected]: 'Rejected',
};

export const getActionTakenText = (
  action: string,
  labelOverrides?: ActionTakenLabelOverrides,
  documentType?: DocumentTemplateType | null,
): string => {
  if (action === 'N/A' || !action) return 'None';
  if (documentType === DocumentTemplateType.PunchList) {
    if (action === ActionTakenType.NoExceptionsTaken) return 'Accepted';
    if (action === ActionTakenType.ReviseAndResubmit) return 'Rejected';
  }
  if (!Object.values(ActionTakenType).includes(action as ActionTakenType)) return 'Unknown Status';
  const key = action as ActionTakenType;
  // Custom labels are submittal-only; every other document type keeps the built-in wording.
  return (
    getCustomActionTakenLabel(key, labelOverrides, documentType) ?? BUILT_IN_ACTION_TAKEN_TEXT[key]
  );
};

export const getDocumentEditFields = (type?: DocumentTemplateType | null) => {
  switch (type) {
    case DocumentTemplateType.Addenda:
      return ['Reference #', 'Addendum #', 'Upload'];
    case DocumentTemplateType.AsiDocuments:
      return ['ASI #', 'Title', 'Description', 'Created By', 'Upload'];
    case DocumentTemplateType.RegulatoryApprovals:
    case DocumentTemplateType.ConstructionChangeDirectives:
      return ['CCD #', 'Title', 'Description', 'Created By', 'Upload'];
    case DocumentTemplateType.ContractorDailyLogs:
      return ['Log Date', 'Title', 'Description'];
    case DocumentTemplateType.FieldReports:
      return ['Field Report #', 'Title', 'General Observation'];
    case DocumentTemplateType.MeetingMinutes:
    case DocumentTemplateType.DesignMeetingMinutes:
      return ['Meeting Date', 'Title', 'Description'];
    case DocumentTemplateType.ChangeOrders:
      return [
        'Title',
        'Change Order #',
        'Description',
        'Change Orders Value',
        'Change Orders Days Added',
        'Upload',
      ];
    case DocumentTemplateType.MiscellaneousDocuments:
      return ['Misc. Document #', 'Title', 'Description'];
    case DocumentTemplateType.Schedules:
      return ['Title', 'Description'];
    case DocumentTemplateType.CertificateOfSubstantialCompletion:
      return ['Description', 'Substantial Completion Date', 'Upload'];
    case DocumentTemplateType.ClearLp:
      return ['Reference #', 'Description', 'Upload'];
    case DocumentTemplateType.PayApplications:
      return ['Pay App #', 'Title', 'Description'];
    case DocumentTemplateType.PotentialChangeOrders:
      return [
        'PCO #',
        'Title',
        'Description',
        'Impact to Cost',
        'Impact to Time',
        'Responsibility',
        'Due',
        'Upload',
      ];
    case DocumentTemplateType.RequestsForChange:
      return [
        'RFC #',
        'Title',
        'Description',
        'Impact to Cost',
        'Impact to Time',
        'Responsibility',
        'Due',
        'Upload',
      ];
    case DocumentTemplateType.Submittals:
      return [
        'Specification Section',
        'Title',
        'Anticipated Initial Submission Date',
        'Due',
        'Responsibility',
        'Upload',
      ];
    case DocumentTemplateType.CloseoutSubmittals:
      return [
        'Specification Section',
        'Title',
        'Anticipated Initial Submission Date',
        'Due',
        'Responsibility',
      ];
    case DocumentTemplateType.SubmittalPackages:
    case DocumentTemplateType.CloseoutSubmittalPackages:
      return ['Package #', 'Title', 'Anticipated Initial Submission Date', 'Due', 'Responsibility'];
    case DocumentTemplateType.Testing:
      return ['Testing #', 'Title', 'Description', 'Agency'];
    case DocumentTemplateType.RequestsForInformation:
      return [
        'RFI #',
        'Title',
        'Description',
        'Due',
        'Submitted',
        'Responsibility',
        'Impact',
        'Upload',
      ];
    case DocumentTemplateType.WorkChangeProposalRequests:
      return ['WCPR #', 'Description', 'Responsibility', 'dueOptional', 'Upload'];
    case DocumentTemplateType.AsBuilt:
      return ['Upload'];
    case DocumentTemplateType.OperationsAndMaintenanceData:
      return ['Reference #', 'Specification Section', 'Description', 'Upload'];
    case DocumentTemplateType.PunchList:
      return ['Reference #', 'Description', 'Location', 'GC', 'Architect', 'Due', 'Upload'];
    case DocumentTemplateType.WarrantyItems:
      return ['Reference #', 'Description', 'Location', 'Subcontractor', 'Images', 'Upload'];
    case DocumentTemplateType.BidDrawings:
    case DocumentTemplateType.Drawings:
      return ['Sheet Number', 'Title', 'Simple Package', 'Upload'];
    case DocumentTemplateType.Specifications:
      return ['Title', 'Upload'];
    case DocumentTemplateType.Task:
    case DocumentTemplateType.CaTask:
      return ['Item #', 'Title', 'Description'];
    case DocumentTemplateType.DesignPackages:
      return ['Item #', 'Title', 'Description'];
    default:
      return ['Reference #', 'Upload'];
  }
};

export const docTypeHasDueDateEditField = (docType?: DocumentTemplateType | null) => {
  return getDocumentEditFields(docType).includes('Due');
};

export const getDocumentCreationFields = (type?: DocumentTemplateType | null) => {
  switch (type) {
    case DocumentTemplateType.Addenda:
      return ['Reference #', 'Addendum #', 'Upload'];
    case DocumentTemplateType.AsiDocuments:
      return ['ASI #', 'Title', 'Description', 'Created By', 'Upload'];
    case DocumentTemplateType.RegulatoryApprovals:
    case DocumentTemplateType.ConstructionChangeDirectives:
      return ['CCD #', 'Title', 'Description', 'Created By', 'Upload'];
    case DocumentTemplateType.ContractorDailyLogs:
      return ['Log Date', 'Title', 'Description', 'Upload'];
    case DocumentTemplateType.FieldReports:
      return ['Date of Observation', 'Title', 'General Observation', 'Upload'];
    case DocumentTemplateType.MeetingMinutes:
    case DocumentTemplateType.DesignMeetingMinutes:
      return ['Meeting Date', 'Title', 'Description', 'Upload'];
    case DocumentTemplateType.ChangeOrders:
      return [
        'Change Order #',
        'Title',
        'Description',
        'Change Orders Value',
        'Change Orders Days Added',
        'Upload',
      ];
    case DocumentTemplateType.MiscellaneousDocuments:
      return ['Misc. Document #', 'Title', 'Description', 'Upload'];
    case DocumentTemplateType.Schedules:
      return ['Title', 'Description', 'Upload'];
    case DocumentTemplateType.CertificateOfSubstantialCompletion:
      return ['Description', 'Substantial Completion Date', 'Upload'];
    case DocumentTemplateType.ClearLp:
      return ['Reference #', 'Description', 'Upload'];
    case DocumentTemplateType.PayApplications:
      return [
        'Pay App #',
        'Title',
        'Description',
        'Responsibility',
        'Pay Application Stage',
        'Dollar Value',
        'Retainage',
        'Upload',
      ];
    case DocumentTemplateType.PotentialChangeOrders:
      return [
        'PCO #',
        'Title',
        'Description',
        'Impact to Cost',
        'Impact to Time',
        'Responsibility',
        'Upload',
      ];
    case DocumentTemplateType.RequestsForChange:
      return [
        'RFC #',
        'Title',
        'Description',
        'Impact to Cost',
        'Impact to Time',
        'Responsibility',
        'Due Date',
        'Upload',
      ];
    case DocumentTemplateType.Submittals:
    case DocumentTemplateType.CloseoutSubmittals:
      return [
        'Specification Section',
        'Specification Section Title',
        'Title',
        'Anticipated Initial Submission Date',
        'Due Date',
        'Priority',
        'Responsibility',
        'Upload',
      ];
    case DocumentTemplateType.Testing:
      return ['Testing #', 'Title', 'Description', 'Agency', 'Upload'];
    case DocumentTemplateType.RequestsForInformation:
      return [
        'RFI #',
        'Title',
        'Description',
        'Due Date',
        'Submitted',
        'Responsibility',
        'Impact',
        'Upload',
      ];
    case DocumentTemplateType.WorkChangeProposalRequests:
      return ['WCPR #', 'Title', 'Description', 'Responsibility', 'Upload'];
    case DocumentTemplateType.AsBuilt:
      return ['Drawings Sheet', 'Upload'];
    case DocumentTemplateType.OperationsAndMaintenanceData:
      return ['Reference #', 'Specification Section', 'Description', 'Upload'];
    case DocumentTemplateType.PunchList:
      return ['Reference #', 'Description', 'Location', 'Responsibility', 'Due Date', 'Upload'];
    case DocumentTemplateType.WarrantyItems:
      return ['Reference #', 'Location', 'Description', 'Subcontractor', 'Images', 'Upload'];
    case DocumentTemplateType.BidDrawings:
    case DocumentTemplateType.Drawings:
      return ['Title', 'Sheet #', 'Upload'];
    case DocumentTemplateType.Task:
    case DocumentTemplateType.CaTask:
      return ['Item #', 'Title', 'Description', 'Priority', 'Due Date', 'Responsibility', 'Upload'];
    case DocumentTemplateType.DesignPackages:
      return ['Item #', 'Title', 'Description', 'Due Date', 'Responsibility', 'Upload'];
    default:
      return ['Reference #', 'Upload'];
  }
};

export const docTypeHasDueDateCreationField = (docType?: DocumentTemplateType | null) => {
  return getDocumentCreationFields(docType).includes('Due Date');
};

export const getDocumentTypesWithReferenceNumber = () => {
  return [
    DocumentTemplateType.Addenda,
    DocumentTemplateType.AsiDocuments,
    DocumentTemplateType.RegulatoryApprovals,
    DocumentTemplateType.ConstructionChangeDirectives,
    DocumentTemplateType.ChangeOrders,
    DocumentTemplateType.ClearLp,
    DocumentTemplateType.DesignPackages,
    DocumentTemplateType.PayApplications,
    DocumentTemplateType.PotentialChangeOrders,
    DocumentTemplateType.RequestsForChange,
    DocumentTemplateType.Testing,
    DocumentTemplateType.RequestsForInformation,
    DocumentTemplateType.WorkChangeProposalRequests,
    DocumentTemplateType.OperationsAndMaintenanceData,
    DocumentTemplateType.WarrantyItems,
    DocumentTemplateType.Task,
    DocumentTemplateType.MiscellaneousDocuments,
    DocumentTemplateType.CaTask,
  ];
};

/**
 * The creation-form labels that stand for a document's referenceNumber — every type names it
 * after its own log ("RFI #", "Item #", …) but they all fill the same field.
 */
export const referenceNumberCreationFields = [
  'Reference #',
  'ASI #',
  'Change Order #',
  'CCD #',
  'WCPR #',
  'PCO #',
  'RFI #',
  'Field Report #',
  'RFC #',
  'Pay App #',
  'Testing #',
  'Item #',
  'Misc. Document #',
];

/**
 * Whether the creation form for this type asks for a reference number, in which case the
 * creation dialog requires one and suggests the next. Derived from the creation fields so it
 * stays in step with the form itself.
 */
export const docTypeHasReferenceNumberCreationField = (docType?: DocumentTemplateType | null) => {
  return getDocumentCreationFields(docType).some((field) =>
    referenceNumberCreationFields.includes(field),
  );
};

export const generatePassword = (length = 10) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let retVal = '';
  for (var i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
};

export const getSubtitle = (
  documentType: DocumentTemplateType,
  document?: INumberedDocumentView | null,
) => {
  const docTypesNoSubtitle = [
    DocumentTemplateType.RequestsForInformation,
    DocumentTemplateType.AsiDocuments,
    DocumentTemplateType.RequestsForChange,
    DocumentTemplateType.SubmittalPackages,
    DocumentTemplateType.CloseoutSubmittalPackages,
    DocumentTemplateType.ChangeOrders,
    DocumentTemplateType.PotentialChangeOrders,
    DocumentTemplateType.Drawings,
    DocumentTemplateType.BidDrawings,
    DocumentTemplateType.Task,
    DocumentTemplateType.CaTask,
    DocumentTemplateType.DesignPackages,
    DocumentTemplateType.Specifications,
    DocumentTemplateType.WarrantyItems,
    DocumentTemplateType.MiscellaneousDocuments,
  ];
  if (documentType === DocumentTemplateType.SubstitutionRequests) return '';
  if (document) {
    if (document.submittalSection && document.submittalSectionDescription)
      return `${addSpacing(document.submittalSection)} ${document.submittalSectionDescription}`;
    if (
      [DocumentTemplateType.DesignMeetingMinutes, DocumentTemplateType.MeetingMinutes].includes(
        documentType,
      )
    ) {
      return document.title || '';
    }
    if (document.referenceNumber && !docTypesNoSubtitle.includes(documentType))
      return `Reference #${document.referenceNumber}`;
  }
  return '';
};

/**
 * The label that starts a document's name. These are the backend's singular names
 * (getUserFriendlyDocumentTemplateNameSingular in backend/internal-api/utils.ts), which the agreed
 * names use; getUserFriendlyDocumentTemplateNameSingular here differs for a few types and is kept
 * for the places that name a type rather than a document.
 */
const DOCUMENT_NAME_LABELS: Record<DocumentTemplateType, string> = {
  [DocumentTemplateType.BidAdvertisement]: 'Bid Advertisement',
  [DocumentTemplateType.BidTabulation]: 'Bid Tabulation',
  [DocumentTemplateType.InformationalItems]: 'Informational Item',
  [DocumentTemplateType.ProjectManual]: 'Project Manual',
  [DocumentTemplateType.SubstitutionRequests]: 'Substitution Request',
  [DocumentTemplateType.RegulatoryApprovals]: 'Regulatory Approval',
  [DocumentTemplateType.BidderRfIs]: 'Bidder RFI',
  [DocumentTemplateType.Addenda]: 'Addendum',
  [DocumentTemplateType.BidDrawings]: 'Bid Drawing',
  [DocumentTemplateType.AsiDocuments]: 'ASI',
  [DocumentTemplateType.ChangeOrders]: 'Change Order',
  [DocumentTemplateType.ConstructionChangeDirectives]: 'Construction Change Directive',
  [DocumentTemplateType.ContractorDailyLogs]: 'Contractor Daily Log',
  [DocumentTemplateType.FieldReports]: 'Field Report',
  [DocumentTemplateType.MeetingMinutes]: 'CA Meeting Minutes',
  [DocumentTemplateType.DesignMeetingMinutes]: 'Design Meeting Minutes',
  [DocumentTemplateType.MiscellaneousDocuments]: 'Miscellaneous',
  [DocumentTemplateType.PayApplications]: 'Pay Application',
  [DocumentTemplateType.PotentialChangeOrders]: 'Potential Change Order',
  [DocumentTemplateType.RequestsForChange]: 'RFC',
  [DocumentTemplateType.RequestsForInformation]: 'RFI',
  [DocumentTemplateType.Schedules]: 'Schedule',
  [DocumentTemplateType.Submittals]: 'Submittal',
  [DocumentTemplateType.Testing]: 'Testing',
  [DocumentTemplateType.WorkChangeProposalRequests]: 'Work Change Proposal Request',
  [DocumentTemplateType.AsBuilt]: 'As Built',
  [DocumentTemplateType.CertificateOfSubstantialCompletion]:
    'Certificate Of Substantial Completion',
  [DocumentTemplateType.ClearLp]: 'Clear LP',
  [DocumentTemplateType.CloseoutSubmittals]: 'Closeout Submittal',
  [DocumentTemplateType.OperationsAndMaintenanceData]: 'Operations And Maintenance Data',
  [DocumentTemplateType.PunchList]: 'Punch List',
  [DocumentTemplateType.WarrantyItems]: 'Warranty Item',
  [DocumentTemplateType.Drawings]: 'Drawing',
  [DocumentTemplateType.Specifications]: 'Specification',
  [DocumentTemplateType.Task]: 'Design Task',
  [DocumentTemplateType.CaTask]: 'CA Task',
  [DocumentTemplateType.DesignPackages]: 'Design Package',
  [DocumentTemplateType.AdditionalReview]: 'Consultant Review',
  [DocumentTemplateType.SubmittalPackages]: 'Submittal Package',
  [DocumentTemplateType.CloseoutSubmittalPackages]: 'Closeout Submittal Package',
  [DocumentTemplateType.PlanholderList]: 'Planholder List',
  [DocumentTemplateType.AsBuiltPackages]: 'As Built Package',
};

/**
 * The types named by their specification section (`Submittal 08 71 00 Door Hardware`, `Substitution Request 08 71 00 Door Hardware`). Another type
 * can carry a section too (a Procore RFI does), but it is not part of that document's name.
 */
const SECTION_NAMED_TYPES = [
  DocumentTemplateType.Submittals,
  DocumentTemplateType.CloseoutSubmittals,
  DocumentTemplateType.Specifications,
  DocumentTemplateType.OperationsAndMaintenanceData,
  DocumentTemplateType.SubstitutionRequests,
];

/** The number part of a document's name: the backend's getUserFriendlyDocumentNumber. */
const getDocumentNameNumber = (
  document: INumberedDocumentView,
  isSectionNamed: boolean,
  docType: DocumentTemplateType,
): string => {
  const revision = document.revisionNumber ? `R${document.revisionNumber}` : undefined;
  // Addenda are numbered by the database alone (nothing sets a reference number).
  if (docType === DocumentTemplateType.Addenda) {
    return _.compact([document.documentNumber, revision]).join('-');
  }
  if (
    docType === DocumentTemplateType.Submittals ||
    docType === DocumentTemplateType.CloseoutSubmittals
  ) {
    // A submittal is numbered by its spec section alone: never by a reference or document number.
    return document.submittalSection
      ? _.compact([
          document.submittalSection,
          document.submittalParagraphNumber || document.paragraphNumber,
          document.submittalSubparagraphNumber,
          revision,
        ]).join('-')
      : '';
  }
  if (docType === DocumentTemplateType.Task || docType === DocumentTemplateType.CaTask) {
    // As the task's page has always written it: the sub-number after the task number.
    return _.compact([
      document.referenceNumber || document.documentNumber,
      document.submittalParagraphNumber,
      revision,
    ]).join('-');
  }
  if (docType === DocumentTemplateType.PayApplications) {
    const stage = getPayApplicationStage(document) === PayApplicationStage.DRAFT_FOR_REVIEW;
    return `${document.referenceNumber}-${stage ? 'Draft' : 'Final'}${
      revision ? `-${revision}` : ''
    }`;
  }
  if (document.referenceNumber) return _.compact([document.referenceNumber, revision]).join('-');
  if (document.sheetNumber) return _.compact([document.sheetNumber, revision]).join('-');
  if (isSectionNamed && document.submittalSection) {
    return _.compact([
      document.submittalSection,
      document.submittalParagraphNumber || document.paragraphNumber,
      document.submittalSubparagraphNumber,
      revision,
    ]).join('-');
  }
  return _.compact([document.documentNumber, revision]).join('-');
};

/** The number part of a document's name, for the places that show it apart from the rest. */
export const getDocumentNumberText = (
  document: INumberedDocumentView,
  docType: DocumentTemplateType,
): string => getDocumentNameNumber(document, SECTION_NAMED_TYPES.includes(docType), docType);

/** A document's date of observation as its name writes it (YYYY-MM-DD), or '' without one. */
export const getDocumentNameDate = (document: INumberedDocumentView): string =>
  document.dateOfObservation ? parseDate(document.dateOfObservation).format('YYYY-MM-DD') : '';

/**
 * A document's name, the same everywhere CENTERLINE shows one: its page, breadcrumbs, search,
 * notifications and the backend's emails (getUserFriendlyDocumentName). Both are checked against
 * docs/query-tool/document-names.json, the formats agreed on 2026-10-28 (document-names.md there).
 * `docType` is the document's own type: a package's, not its log's.
 */
export const getDocumentName = (
  document: INumberedDocumentView,
  docType: DocumentTemplateType,
): string => {
  const label = DOCUMENT_NAME_LABELS[docType] || 'Unknown Document Type';
  return _.compact([label, getDocumentNameIdentifier(document, docType)]).join(' ');
};

/** A document's name without its type label: its number, date, location or section and title. */
export const getDocumentNameIdentifier = (
  document: INumberedDocumentView,
  docType: DocumentTemplateType,
): string => {
  const isSectionNamed = SECTION_NAMED_TYPES.includes(docType);
  const number = getDocumentNameNumber(document, isSectionNamed, docType);
  const numbered = number ? `#${number}` : '';
  const date = getDocumentNameDate(document);
  const building = document.physicalLocation?.floor?.building;
  let identifier: string;

  if (docType === DocumentTemplateType.Task || docType === DocumentTemplateType.CaTask) {
    // An em dash before the title.
    identifier = _.compact([numbered, document.title ? `— ${document.title}` : '']).join(' ');
  } else if (docType === DocumentTemplateType.ContractorDailyLogs) {
    identifier = _.compact([numbered, date]).join(' - ');
  } else if (
    (docType === DocumentTemplateType.FieldReports ||
      docType === DocumentTemplateType.MeetingMinutes ||
      docType === DocumentTemplateType.DesignMeetingMinutes) &&
    date
  ) {
    identifier = date;
  } else if (docType === DocumentTemplateType.PunchList && building) {
    identifier = _.compact([
      building.name.substr(0, 50),
      document.physicalLocation?.floor?.name.substr(0, 50),
      document.physicalLocation?.name.substr(0, 50),
      (document.description ?? '').substr(0, 50),
    ]).join(' - ');
  } else if (docType === DocumentTemplateType.PayApplications) {
    identifier = numbered;
  } else if (docType === DocumentTemplateType.CertificateOfSubstantialCompletion) {
    identifier = '';
  } else if (isSectionNamed && document.submittalSection) {
    identifier = _.compact([number, document.submittalSectionDescription]).join(' ');
    if (document.title) identifier += ` - ${document.title}`;
  } else if (isSectionNamed && document.submittalSectionDescription) {
    identifier = _.compact([number, document.submittalSectionDescription]).join(' - ');
  } else if (document.title) {
    identifier = numbered ? `${numbered} - ${document.title}` : `- ${document.title}`;
  } else {
    identifier = numbered;
  }

  return identifier;
};

/** The document's own type: its template's, which for a package differs from the log's `docType`. */
const documentNameType = (docType: DocumentTemplateType, document: INumberedDocumentView) =>
  (document.documentTemplate?.name as DocumentTemplateType | undefined) || docType;

export const getTitle = (
  docType: DocumentTemplateType,
  document: INumberedDocumentView | null,
  bid?: IBid | null,
) => {
  if (bid) {
    return `Bid Submission #${bid.bidNumber}`;
  }
  if (!document) return 'N/A';
  return getDocumentName(document, documentNameType(docType, document));
};

export const isPublicPage = () => {
  const key = window.location.pathname.split('/').slice(2, 3).join('/');
  return (
    key === 'public' ||
    key === 'register' ||
    (key === 'pdf' && localStorage.getItem('publicAccessKey') !== null)
  );
};

export const isLoginPage = () => {
  const key = window.location.pathname.split('/').slice(2, 3).join('/');
  return key === 'login';
};

export const isPdfViewer = () =>
  window.location.pathname.split('/').slice(2, 3).join('/') === 'pdf';

let publicPageRedirect: null | string = null;
export const getPublicPageRedirect = () => {
  return publicPageRedirect;
};
export const setPublicPageRedirect = (str: string | null) => {
  publicPageRedirect = str;
};

export const getUserFriendlyDocumentTemplateNameSingular = (
  documentTemplateType: DocumentTemplateType,
) => {
  switch (documentTemplateType) {
    case DocumentTemplateType.BidAdvertisement:
      return 'Bid Advertisement';
    case DocumentTemplateType.BidTabulation:
      return 'Bid Tabulation';
    case DocumentTemplateType.InformationalItems:
      return 'Informational Item';
    case DocumentTemplateType.ProjectManual:
      return 'Project Manual';
    case DocumentTemplateType.SubstitutionRequests:
      return 'Substitution Request';
    case DocumentTemplateType.RegulatoryApprovals:
      return 'Regulatory Approval';
    case DocumentTemplateType.BidderRfIs:
      return 'Bidder RFI';
    case DocumentTemplateType.Addenda:
      return 'Addendum';
    case DocumentTemplateType.BidDrawings:
      return 'Bid Drawing';
    case DocumentTemplateType.AsiDocuments:
      return 'ASI';
    case DocumentTemplateType.ChangeOrders:
      return 'Change Order';
    case DocumentTemplateType.ConstructionChangeDirectives:
      return 'Construction Change Directive';
    case DocumentTemplateType.ContractorDailyLogs:
      return 'Contractor Daily Log';
    case DocumentTemplateType.FieldReports:
      return 'Field Report';
    case DocumentTemplateType.MeetingMinutes:
      return 'CA Meeting Minutes';
    case DocumentTemplateType.DesignMeetingMinutes:
      return 'Design Meeting Minutes';
    case DocumentTemplateType.MiscellaneousDocuments:
      return 'Miscellaneous';
    case DocumentTemplateType.PayApplications:
      return 'Pay Application';
    case DocumentTemplateType.PotentialChangeOrders:
      return 'Potential Change Order';
    case DocumentTemplateType.RequestsForChange:
      return 'Request For Change';
    case DocumentTemplateType.RequestsForInformation:
      return 'Request For Information';
    case DocumentTemplateType.Schedules:
      return 'Schedule';
    case DocumentTemplateType.Submittals:
      return 'Submittal';
    case DocumentTemplateType.Testing:
      return 'Testing';
    case DocumentTemplateType.WorkChangeProposalRequests:
      return 'Work Change Proposal Request';
    case DocumentTemplateType.AsBuilt:
      return 'As-Built';
    case DocumentTemplateType.CertificateOfSubstantialCompletion:
      return 'Certificate Of Substantial Completion';
    case DocumentTemplateType.ClearLp:
      return 'Clear LP';
    case DocumentTemplateType.CloseoutSubmittals:
      return 'Closeout Submittal';
    case DocumentTemplateType.OperationsAndMaintenanceData:
      return 'Operations And Maintenance Data';
    case DocumentTemplateType.PunchList:
      return 'Punch List Item';
    case DocumentTemplateType.WarrantyItems:
      return 'Warranty Item';
    case DocumentTemplateType.Drawings:
      return 'Drawing';
    case DocumentTemplateType.Specifications:
      return 'Specification';
    case DocumentTemplateType.Task:
      return 'Design Task';
    case DocumentTemplateType.CaTask:
      return 'CA Task';
    case DocumentTemplateType.DesignPackages:
      return 'Design Package';
    case DocumentTemplateType.AdditionalReview:
      return 'Consultant Review';
    case DocumentTemplateType.SubmittalPackages:
      return 'Submittal Package';
    case DocumentTemplateType.CloseoutSubmittalPackages:
      return 'Closeout Submittal Package';
    case DocumentTemplateType.PlanholderList:
      return 'Planholder List';
    case DocumentTemplateType.AsBuiltPackages:
      return 'As-Built Package';
    default:
      console.warn(`Unknown document type: ${documentTemplateType}`);
      return 'Unknown Document Type';
  }
};

export const getUserFriendlyDocumentTemplateNamePlural = (
  documentTemplateType: DocumentTemplateType,
) => {
  switch (documentTemplateType) {
    case DocumentTemplateType.BidAdvertisement:
      return 'Bid Advertisements';
    case DocumentTemplateType.BidTabulation:
      return 'Bid Tabulations';
    case DocumentTemplateType.InformationalItems:
      return 'Informational Items';
    case DocumentTemplateType.ProjectManual:
      return 'Project Manuals';
    case DocumentTemplateType.SubstitutionRequests:
      return 'Substitution Requests';
    case DocumentTemplateType.RegulatoryApprovals:
      return 'Regulatory Approvals';
    case DocumentTemplateType.BidderRfIs:
      return 'Bidder RFIs';
    case DocumentTemplateType.Addenda:
      return 'Addendas';
    case DocumentTemplateType.BidDrawings:
      return 'Bid Drawings';
    case DocumentTemplateType.AsiDocuments:
      return 'ASIs';
    case DocumentTemplateType.ChangeOrders:
      return 'Change Orders';
    case DocumentTemplateType.ConstructionChangeDirectives:
      return 'Construction Change Directives';
    case DocumentTemplateType.ContractorDailyLogs:
      return 'Contractor Daily Logs';
    case DocumentTemplateType.FieldReports:
      return 'Field Reports';
    case DocumentTemplateType.MeetingMinutes:
      return 'CA Meeting Minutes';
    case DocumentTemplateType.DesignMeetingMinutes:
      return 'Design Meeting Minutes';
    case DocumentTemplateType.MiscellaneousDocuments:
      return 'Miscellaneous';
    case DocumentTemplateType.PayApplications:
      return 'Pay Applications';
    case DocumentTemplateType.PotentialChangeOrders:
      return 'Potential Change Orders';
    case DocumentTemplateType.RequestsForChange:
      return 'Requests For Change';
    case DocumentTemplateType.RequestsForInformation:
      return 'Requests For Information';
    case DocumentTemplateType.Schedules:
      return 'Schedules';
    case DocumentTemplateType.Submittals:
      return 'Submittals';
    case DocumentTemplateType.Testing:
      return 'Testing';
    case DocumentTemplateType.WorkChangeProposalRequests:
      return 'Work Change Proposal Requests';
    case DocumentTemplateType.AsBuilt:
      return 'As Builts';
    case DocumentTemplateType.CertificateOfSubstantialCompletion:
      return 'Certificates Of Substantial Completion';
    case DocumentTemplateType.ClearLp:
      return 'Clear LPs';
    case DocumentTemplateType.CloseoutSubmittals:
      return 'Closeout Submittals';
    case DocumentTemplateType.OperationsAndMaintenanceData:
      return 'Operations And Maintenance Data';
    case DocumentTemplateType.PunchList:
      return 'Punch List Items';
    case DocumentTemplateType.WarrantyItems:
      return 'Warranty Items';
    case DocumentTemplateType.Drawings:
      return 'Drawings';
    case DocumentTemplateType.Specifications:
      return 'Specifications';
    case DocumentTemplateType.Task:
      return 'Design Tasks';
    case DocumentTemplateType.CaTask:
      return 'CA Tasks';
    case DocumentTemplateType.DesignPackages:
      return 'Design Packages';
    case DocumentTemplateType.AdditionalReview:
      return 'Consultant Reviews';
    case DocumentTemplateType.SubmittalPackages:
      return 'Submittal Packages';
    case DocumentTemplateType.CloseoutSubmittalPackages:
      return 'Closeout Submittal Packages';
    case DocumentTemplateType.PlanholderList:
      return 'Planholder List';
    default:
      console.warn(`Unknown document type: ${documentTemplateType}`);
      return 'Unknown Document Type';
  }
};

/**
 * A submittal's name without its type label (the label is the caller's): the number the shared name
 * uses -- its spec section, paragraph, subparagraph and revision -- and, with `withTitle`, the
 * section name and title as the name writes them.
 */
export const getSubmittalTitle = (submittal: INumberedDocumentView, withTitle = true) => {
  const type = documentNameType(DocumentTemplateType.Submittals, submittal);
  return withTitle
    ? getDocumentNameIdentifier(submittal, type)
    : getDocumentNumberText(submittal, type);
};

export const isLocal = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  return !!apiUrl && apiUrl.includes('localhost');
};

export const isDev = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  return !!apiUrl && (apiUrl.includes('localhost') || apiUrl.includes('dev'));
};

export const generateProcoreLoginLink = () => {
  return `https://login.procore.com/oauth/authorize?response_type=code&client_id=${
    process.env.REACT_APP_PROCORE_CLIENT_ID
  }&redirect_uri=${encodeURIComponent(process.env.REACT_APP_PROCORE_CALLBACK_URL!)}`;
};

export const getTomorrowDate = () => {
  const date = dayjs().toDate();
  date.setDate(date.getDate() + 1);
  return date;
};

export function onlyUniqueByProperty<T>(value: T, index: number, self: T[], property: keyof T) {
  return self.findIndex((item) => item[property] === value[property]) === index;
}

export const isSimpleWorkflowDoc = (docType: DocumentTemplateType | undefined | null) => {
  if (!docType) return false;
  switch (docType) {
    case DocumentTemplateType.SubstitutionRequests:
    case DocumentTemplateType.BidderRfIs:
    case DocumentTemplateType.PayApplications:
    case DocumentTemplateType.WorkChangeProposalRequests:
    case DocumentTemplateType.PunchList:
    case DocumentTemplateType.PotentialChangeOrders:
    case DocumentTemplateType.RequestsForChange:
    case DocumentTemplateType.RequestsForInformation:
    case DocumentTemplateType.AsBuilt:
      return true;
    default:
      return false;
  }
};

export const isPdf = (name?: string) =>
  !!name && name.slice(name.length - 3).toLowerCase() === 'pdf';

export const generateUniqueId = () => {
  return v4();
};

export const removeNewlines = (text?: string): string | undefined =>
  text?.replace(/(\r\n|\n|\r)/gm, ' ').replace(/\s+/g, ' ');

export { sortLastNamesAsc, sortLastNamesDesc } from './name-utils';
export { getProjectDisplayName, projectSort } from './project-name-utils';

export const nodeIsSubfolder = (node?: FileNode) =>
  !!node && (node.fullKey.match(/\//g) || []).length > 2;

export const canViewDesign = (
  //Required params
  productPackage?: IProductPackage | null,
  security?: ISecurityGroup | null,
  //Optional params for determining if admin
  user?: IUser,
  currentProject?: Project,
) => {
  //If admin
  if (
    user &&
    currentProject &&
    (user.adminOfSubscriberId === currentProject.subscriberId || user.isSiteAdmin)
  ) {
    return true;
  }
  if (security === null || !productPackage?.canViewDesign) {
    return false;
  } else if (security === undefined) {
    return productPackage.canViewDesign;
  } else {
    const { designTabPermission } = security;
    return (
      designTabPermission !== null &&
      designTabPermission !== undefined &&
      designTabPermission >= SecurityPermissionLevel.NUMBER_0 &&
      productPackage.canViewDesign
    );
  }
};

export const canViewBidding = (productPackage?: IProductPackage | null) => {
  if (!productPackage?.canViewBidding) return false;
  return productPackage.canViewBidding;
};

export const canViewConstructionAndCloseout = (productPackage?: IProductPackage | null) => {
  if (!productPackage?.canViewConstructionAndCloseout) return false;
  return productPackage.canViewConstructionAndCloseout;
};

export const getFilteredUsersFromUserGroup = (
  type: ManagePermissionsDialogType,
  group?: IUserGroup | null,
  /** See getUserPermissionForCurrentDocType's docTypeOverride. */
  docTypeOverride?: DocumentTemplateType,
) => {
  return getUsersFromUserGroup(group).filter((user) =>
    hasPermissionToView(type, user, docTypeOverride),
  );
};

export const getUserCompanyName = (
  user?: {
    companyName?: string | null;
    company?: { name: string } | null;
    publicCompanyName?: string | null;
  } | null,
  fallback = 'Unknown Company',
) => {
  if (!user) return fallback;
  return user.companyName || user.company?.name || user.publicCompanyName || fallback;
};

export const getCurrentlyResponsibleUsers = (
  document?: INumberedDocumentView | null,
): { id?: string | null; name: string }[] => {
  if (!document || document.actionTaken === ActionTakenType.Withdrawn)
    return [{ name: 'Not Assigned' }];
  const {
    workflowStatus,
    generalContractorUserId,
    generalContractorUser,
    architectUserId,
    architectUser,
    consultantSummary,
  } = document;

  const generalContractor = generalContractorUserId
    ? {
        id: generalContractorUserId,
        name: generalContractorUser?.name || '',
      }
    : { name: 'Not Assigned' };
  const architect = architectUserId
    ? {
        id: architectUserId,
        name: architectUser?.name || '',
      }
    : { name: 'Not Assigned' };

  switch (workflowStatus) {
    case WorkflowStatusType.Initial:
    case WorkflowStatusType.ReadyForSubmissionToArchitect:
      if (document.isWorkflowInverted) {
        return [architect];
      }
      return [generalContractor];

    case WorkflowStatusType.SubmittedForReview:
    case WorkflowStatusType.UnderReview:
    case WorkflowStatusType.ArchitectUploaded:
      if (
        consultantSummary?.numConsultantReviewsResolved &&
        consultantSummary.numConsultantReviewsResolved > 0
      ) {
        if (document.isWorkflowInverted) {
          return [generalContractor];
        }
        return [architect];
      }
      if (document.isWorkflowInverted) {
        return [generalContractor];
      }
      return [architect];

    default:
      break;
  }
  return [{ name: 'Not Assigned' }];
};

export const getCurrentlyResponsibleCompany = (document?: INumberedDocumentView | null): string => {
  if (!document || document.actionTaken === ActionTakenType.Withdrawn) return '';
  const { workflowStatus, generalContractorUser, architectUser, consultantSummary } = document;

  const generalContractor = generalContractorUser?.company?.name || '';
  const architect = architectUser?.company?.name || '';

  switch (workflowStatus) {
    case WorkflowStatusType.Initial:
    case WorkflowStatusType.ReadyForSubmissionToArchitect:
      if (document.isWorkflowInverted) {
        return architect;
      }
      return generalContractor;

    case WorkflowStatusType.SubmittedForReview:
    case WorkflowStatusType.UnderReview:
    case WorkflowStatusType.ArchitectUploaded:
      if (
        consultantSummary?.numConsultantReviewsResolved &&
        consultantSummary.numConsultantReviewsResolved > 0
      ) {
        if (document.isWorkflowInverted) {
          return generalContractor;
        }
        return architect;
      }
      if (document.isWorkflowInverted) {
        return generalContractor;
      }
      return architect;

    default:
      break;
  }
  return '';
};

type ConsultantReviewSummaryRow = {
  id: string;
  name: string;
  workflowStatus: WorkflowStatusType;
  companyName: string;
  dueDate: string;
};

/**
 * Workflow statuses that close an individual consultant review, i.e. the review is no longer
 * sitting with the consultant:
 *  - ReviewComplete -- the consultant returned it, and it now awaits the architect;
 *  - Accepted / Resolved -- the architect accepted or adopted it (the two backend responses that
 *    finish a review, see respondToReview);
 *  - Resubmitted -- the architect returned it for another pass, which supersedes this row with a
 *    fresh revision carrying the same consultant (insertRevisionOfDocument); the successor is the
 *    row that is open, not this one;
 *  - Canceled -- it was canceled out from under the consultant.
 *
 * Shared by the "Responsible Consultant" column and the consultant pills in DocumentIndexRow's
 * getConsultantBadge so the two always agree on who is still owed work. Closed is deliberately
 * NOT the same as complete: the "Consultant Review Complete" pill exists to tell the architect a
 * review awaits their acceptance or rejection, so only ReviewComplete earns it.
 */
export const CONSULTANT_REVIEW_CLOSED_STATUSES: string[] = [
  WorkflowStatusType.ReviewComplete,
  WorkflowStatusType.Accepted,
  WorkflowStatusType.Resolved,
  WorkflowStatusType.Resubmitted,
  WorkflowStatusType.Canceled,
];

export const consultantReviewIsOpen = (workflowStatus?: WorkflowStatusType | string | null) =>
  !workflowStatus || !CONSULTANT_REVIEW_CLOSED_STATUSES.includes(workflowStatus);

const getOpenConsultantReviews = (document?: INumberedDocumentView | null) => {
  const summaryByReview = (document?.consultantSummary?.summaryByReview ||
    []) as ConsultantReviewSummaryRow[];
  return summaryByReview.filter((row) => consultantReviewIsOpen(row.workflowStatus));
};

/**
 * Workflow statuses that mean the document's own review is over. Mirrors the set the consultant
 * pills use in DocumentIndexRow's getConsultantBadge -- once the document reaches one of these,
 * a consultant review left open on it no longer says anything about who owes work.
 */
const DOCUMENT_REVIEW_CLOSED_STATUSES: string[] = [
  WorkflowStatusType.ReviewComplete,
  WorkflowStatusType.Completed,
  WorkflowStatusType.Resolved,
  WorkflowStatusType.Resubmitted,
  WorkflowStatusType.Accepted,
  WorkflowStatusType.Canceled,
];

/**
 * Action taken values that close a document out without a review outcome. A withdrawn or
 * for-information-only document is never under review either, whatever its workflow status says.
 */
const DOCUMENT_REVIEW_CLOSED_ACTIONS: string[] = [
  ActionTakenType.Withdrawn,
  ActionTakenType.ForInformationOnly,
];

export const documentIsNoLongerUnderReview = (
  document?: Pick<INumberedDocumentView, 'workflowStatus' | 'actionTaken'> | null,
): boolean =>
  (!!document?.workflowStatus &&
    DOCUMENT_REVIEW_CLOSED_STATUSES.includes(document.workflowStatus)) ||
  (!!document?.actionTaken && DOCUMENT_REVIEW_CLOSED_ACTIONS.includes(document.actionTaken));

// The "Responsible Consultant" column: unlike getCurrentlyResponsibleUsers/Company (which never
// show a consultant), this ONLY ever shows consultants -- and only while their review is open. It
// falls back to the "Not Assigned" empty state once every review is closed, once the document
// itself is no longer under review (regardless of any review's own status), or if none are
// assigned.
export const getResponsibleConsultantUsers = (
  document?: INumberedDocumentView | null,
): { id?: string | null; name: string }[] => {
  if (documentIsNoLongerUnderReview(document)) return [{ name: 'Not Assigned' }];
  const openReviews = _.uniqBy(getOpenConsultantReviews(document), 'id');
  if (!openReviews.length) return [{ name: 'Not Assigned' }];
  return openReviews.map((row) => ({ id: row.id, name: row.name }));
};

export const getResponsibleConsultantCompany = (
  document?: INumberedDocumentView | null,
): string => {
  if (documentIsNoLongerUnderReview(document)) return '';
  return getOpenConsultantReviews(document)[0]?.companyName || '';
};

export const getPageTitle = (
  docType: DocumentTemplateType,
  document: INumberedDocumentView | null | undefined,
  bid?: IBid | null,
) => {
  if (bid) {
    return `Bid Submission #${bid.bidNumber}`;
  }
  if (!document) return 'N/A';
  return getDocumentName(document, documentNameType(docType, document));
};

export const copyToClipboard = async (text: string) => {
  if (navigator.clipboard !== undefined) await navigator.clipboard.writeText(text);
  else {
    console.log('navigator.clipboard is undefined, trying fallback...');
    if (window.document.queryCommandSupported && window.document.queryCommandSupported('copy')) {
      const textArea = window.document.createElement('textarea');

      // Place in the top-left corner of screen regardless of scroll position.
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';

      // Ensure it has a small width and height. Setting to 1px / 1em
      // doesn't work as this gives a negative w/h on some browsers.
      textArea.style.width = '2em';
      textArea.style.height = '2em';

      // We don't need padding, reducing the size if it does flash render.
      textArea.style.padding = '0';

      // Clean up any borders.
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';

      // Avoid flash of the white box if rendered for any reason.
      textArea.style.background = 'transparent';

      textArea.value = text;

      window.document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      window.document.execCommand('copy');

      window.document.body.removeChild(textArea);
    }
  }
};

export const getParentFromFullKey = (fullKey: string) => {
  const keys = fullKey.split('/').filter((k) => k);
  if (keys.length === 1) return '/';
  keys.pop();
  return keys[keys.length - 1];
};

export const areFilesInSameParentFolder = (files: FileNode[]) => {
  if (files.length <= 1) return true;
  const parentKeys = files.map((f) => getParentFromFullKey(f.fullKey));
  return parentKeys.every((key, index, array) => array[0] === key);
};

export const sleep = (ms: number) => {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
};

const VERIFY_WAIT_TIME_MILLISECONDS = 30000;
const VERIFY_WAIT_TIME_MILLISECONDS_BLUEBEAM = 300000;

export const waitForFileToBeVerified = async (file: IFile, isBluebeam = false) => {
  if (file.isVerified) return true;
  const timeoutLength = isBluebeam
    ? VERIFY_WAIT_TIME_MILLISECONDS_BLUEBEAM
    : VERIFY_WAIT_TIME_MILLISECONDS;

  // Give the file a chance to be verified first
  await sleep(1000);

  const startTime = Date.now();
  return new Promise<boolean>(async (resolve) => {
    while (Date.now() < startTime + timeoutLength) {
      const { isVerified } = await getFileById(file.id);
      if (isVerified) {
        resolve(true);
        break;
      } else await sleep(5000);
    }
    resolve(false);
  });
};

export const waitForFileToBeVerifiedByUrl = async (url: string) => {
  const timeoutLength = VERIFY_WAIT_TIME_MILLISECONDS;

  const startTime = Date.now();
  return new Promise<boolean>(async (resolve) => {
    while (Date.now() < startTime + timeoutLength) {
      const isVerified = await verifyFileByUrl(url);
      if (isVerified) {
        resolve(true);
        break;
      } else await sleep(5000);
    }
    resolve(false);
  });
};

export const getUserFriendlyStringForDesignActionTakenType = (
  action: DesignActionTakenType,
  isFolder = false,
) => {
  switch (action) {
    case DesignActionTakenType.UploadedFile:
      return 'uploaded this file';

    case DesignActionTakenType.CreatedFolder:
      return 'created this folder';

    case DesignActionTakenType.DownloadedFile:
      return 'downloaded this file';

    case DesignActionTakenType.ViewedFile:
      return 'viewed this file';

    case DesignActionTakenType.DeletedNode:
      return `deleted this ${isFolder ? 'folder' : 'file'}`;

    case DesignActionTakenType.SharedNode:
      // return `shared this ${isFolder ? 'folder' : 'file'}`;
      return 'shared with';

    case DesignActionTakenType.SentDownloadLink:
      return 'sent a download link to';

    case DesignActionTakenType.ModifiedNode:
      return `renamed this ${isFolder ? 'folder' : 'file'}`;

    case DesignActionTakenType.AccessNotification:
      return 'notified';

    case DesignActionTakenType.SentPublicFolderAccessLink:
      return 'sent a public access link to';

    default:
      break;
  }
};

export const getFileOrFolderNameFromFullKey = (fullKey: string) => {
  const fullKeySplit = fullKey.split('/');
  const fileName = fullKeySplit.pop();
  if (fileName) return fileName;
  return fullKeySplit.pop();
};

export const computeOptimalImageSize = (
  initialWidth: number,
  initialHeight: number,
  maxWidth: number,
  maxHeight: number,
  zoomFactor = 1.5,
) => {
  let newWidth = initialWidth;
  let newHeight = initialHeight;
  let zoomCount = 0;

  while (
    newWidth * zoomFactor <= maxWidth &&
    newHeight * zoomFactor <= maxHeight &&
    zoomCount < 5
  ) {
    newWidth = newWidth * zoomFactor;
    newHeight = newHeight * zoomFactor;
    zoomCount += 1;
  }

  return { width: newWidth, height: newHeight };
};

export const getPreviousDocumentRevisions = (document: INumberedDocumentView) => {
  if (!documentIsRevision(document)) return [];
  if (!document.associatedDocumentsList) return [];
  const revisions = document.associatedDocumentsList
    .filter(({ associatedDocument }) => {
      if (!associatedDocument) return false;
      if (associatedDocument.documentNumber !== document.documentNumber) return false;
      if (document.revisionNumber === 1) return !associatedDocument.revisionNumber;
      return associatedDocument.revisionNumber === document.revisionNumber! - 1;
    })
    .map(({ associatedDocument }) => associatedDocument) as INumberedDocumentView[];
  return revisions.sort((a, b) => descendingComparator(a, b, 'createdOn')) || [];
};

export const getNewerDocumentRevisions = (document: INumberedDocumentView) => {
  if (!document.reverseAssociatedDocumentsList) return [];
  const revisions = document.reverseAssociatedDocumentsList
    .filter(({ baseDocument }) => {
      if (!baseDocument) return false;
      if (baseDocument.documentNumber !== document.documentNumber) return false;
      return !!baseDocument.revisionNumber && baseDocument.revisionNumber > 0;
    })
    .map(({ baseDocument }) => baseDocument) as INumberedDocumentView[];
  return revisions.sort((a, b) => descendingComparator(a, b, 'createdOn')) || [];
};

export const getUserFriendlyParserJobStatusType = (status: ExpandedJobStatusType) => {
  switch (status) {
    case ExpandedJobStatusType.CreatingJob:
    case ExpandedJobStatusType.Pending:
      return 'Loading';

    case ExpandedJobStatusType.Initiated:
      return 'Parsing';

    case ExpandedJobStatusType.Complete:
      return 'Success';

    case ExpandedJobStatusType.JobCreationFailed:
    case ExpandedJobStatusType.Canceled:
      return 'Failed';

    default:
      return 'Loading';
  }
};

// As-Built is not a submittal type, so custom labels never apply here.
export const getAsBuiltStatus = (document: INumberedDocumentView) => {
  if (document.workflowStatus === WorkflowStatusType.Completed) {
    if (document.actionTaken) return getActionTakenText(document.actionTaken);
    return 'Completed';
  }
  return document.workflowStatus ? statusToGeneralString[document.workflowStatus] : 'N/A';
};

export const parseDate = (date: string) => {
  const dateJs = parseDateHelper(date);
  if (dateJs.utc(false).format('HH:mm:ss.SSS') === '00:00:00.000') {
    return dateJs.utc(false);
  }
  return dateJs;
};

const parseDateHelper = (date: string) => {
  let returnDate = dayjs(date);
  if (returnDate.isValid()) return returnDate;

  const dateParsed = date.replaceAll('-', '/');
  const split = dateParsed.split('.');
  if (!split[1]) {
    returnDate = dayjs(dateParsed);
    if (returnDate.isValid()) return returnDate;
  }

  return dayjs(`${split[0]}+00`);
};

/**
 * Joins the parts of a document's label with " - ", leaving out the ones that are not set. These
 * fields are each optional, and interpolating an unset one straight into a template literal put
 * the word "null" in front of users.
 */
const joinTitleParts = (parts: (string | null | undefined)[]) =>
  parts
    .map((part) => part?.toString().trim())
    .filter((part) => !!part)
    .join(' - ');

export const getDocumentTitleForAssociations = (
  document: INumberedDocumentView,
  type: DocumentTemplateType,
  inDialog = true,
): string => {
  let title = '';
  switch (type) {
    case DocumentTemplateType.Specifications:
      if (document.submittalSection && document.submittalSectionDescription)
        title = `${addSpacing(document.submittalSection!)} - ${
          document.submittalSectionDescription
        }`;
      else title = document.title || '';
      break;

    case DocumentTemplateType.Submittals:
    case DocumentTemplateType.CloseoutSubmittals:
      title = document.submittalSection
        ? joinTitleParts([
            getSubmittalTitle(document, false),
            document.submittalSectionDescription,
            document.title,
          ])
        : document.title || '';
      break;

    case DocumentTemplateType.Drawings:
    case DocumentTemplateType.AsBuilt:
      title = joinTitleParts([document.sheetNumber || 'N/A', document.title]);
      break;

    case DocumentTemplateType.AsBuiltPackages:
    case DocumentTemplateType.SubmittalPackages:
    case DocumentTemplateType.CloseoutSubmittalPackages:
      title = joinTitleParts([document.referenceNumber, document.title]);
      break;

    default:
      return getTitle(type, document);
  }

  if (inDialog) return title;
  return `${getUserFriendlyDocumentTemplateNameSingular(type)} ${title}`;
};

export type DocumentFileTreeDocument = {
  id: string;
  title: string;
  /**
   * The document's name as the rest of the product writes it -- getPageTitle, the same helper
   * behind page headers and the notification inbox, so it carries the document's type and reads
   * as a name on its own. `title` is tuned for the tree, where the type is already the heading
   * above it, so a caller that lifts an entry out of the tree wants this instead. Absent on
   * entries that are not documents.
   */
  userFriendlyName?: string;
  isPackage?: boolean;
  checked: boolean;
  files?: DocumentFileTreeDocument[];
};

export type DocumentFileTree = {
  type: string;
  documents: DocumentFileTreeDocument[];
};

/**
 * The tree types the Share Anything card uses for the project's storytelling forms. They are not
 * documents and have no DocumentTemplateType, so they ride in trees of their own; each entry's id
 * is `<formType>:<formId or empty>`, which is what the share sends back.
 *
 * The forms a project can have many of get a type each, so they group under their own heading the
 * way documents of one type do. The ones it only ever has one of share the type below, named for
 * the phase itself, which the picker lists directly under the phase.
 */
export const STORYTELLING_TREE_TYPE = 'Storytelling';
export const STORYTELLING_PROGRESS_TREE_TYPE = 'Project Progress Reports';
export const STORYTELLING_QC_TREE_TYPE = 'Quality Control Forms';

export const STORYTELLING_TREE_TYPES = [
  STORYTELLING_TREE_TYPE,
  STORYTELLING_PROGRESS_TREE_TYPE,
  STORYTELLING_QC_TREE_TYPE,
];

/**
 * Which phase each document type belongs to, for the pickers that group by phase. Types the tree
 * folds into another (submittal packages into Submittals, for one) never appear as a tree type,
 * so listing them here is harmless.
 */
export const DOCUMENT_PHASES: { title: string; types: string[] }[] = [
  { title: 'Design', types: DOCUMENT_TYPES_DESIGN },
  { title: 'Bidding', types: DOCUMENT_TYPES_BIDDING },
  { title: 'Construction', types: DOCUMENT_TYPES_CONSTRUCTION },
  { title: 'Close Out', types: DOCUMENT_TYPES_CLOSE_OUT },
  { title: 'Storytelling', types: STORYTELLING_TREE_TYPES },
];

const documentColumnsForAssociations: (keyof INumberedDocumentView)[] = [
  'id',
  'documentTemplateId',
  'submittalSection',
  'submittalSectionDescription',
  'title',
  'sheetNumber',
  'referenceNumber',
  'documentNumber',
  'revisionNumber',
  'dateOfObservation',
  'description',
];

/**
 * The project's documents, grouped by type, for the dialogs that pick documents.
 *
 * `includeAllPhases` widens the set to the design and bidding types left out below. Those are
 * excluded by default because this list started as the "tag an existing document" picker, where
 * they are not taggable. The Share Anything card passes it: a share sends a public link, and a
 * design task or an addendum is as shareable as anything else.
 */
export const getDocumentTreesForAssociations = async (
  projectId: string,
  documentIdsToRemove?: string[],
  documentTypesFilter?: DocumentTemplateType[],
  includeAllPhases = false,
) => {
  const newDocuments: DocumentFileTree[] = [];
  const templates = await getTemplateIds();
  let allDocuments = await getDocumentsByProjectId(projectId, documentColumnsForAssociations);

  if (documentIdsToRemove) {
    allDocuments = allDocuments.filter((d) => !documentIdsToRemove.includes(d.id));
  }

  allDocuments.forEach((document) => {
    const trueDocumentType = templates[document.documentTemplateId] as DocumentTemplateType;
    let documentType = trueDocumentType;

    // AdditionalReview is left out either way: a review is not a document of its own to the
    // reader, it belongs to the document it reviews.
    if (trueDocumentType === DocumentTemplateType.AdditionalReview) return null;

    if (
      !includeAllPhases &&
      (trueDocumentType === DocumentTemplateType.Task ||
        trueDocumentType === DocumentTemplateType.CaTask ||
        trueDocumentType === DocumentTemplateType.DesignPackages ||
        trueDocumentType === DocumentTemplateType.BidDrawings ||
        trueDocumentType === DocumentTemplateType.ProjectManual ||
        trueDocumentType === DocumentTemplateType.Addenda ||
        trueDocumentType === DocumentTemplateType.InformationalItems ||
        trueDocumentType === DocumentTemplateType.SubstitutionRequests ||
        trueDocumentType === DocumentTemplateType.BidderRfIs ||
        trueDocumentType === DocumentTemplateType.PlanholderList ||
        trueDocumentType === DocumentTemplateType.BidTabulation ||
        trueDocumentType === DocumentTemplateType.RegulatoryApprovals ||
        trueDocumentType === DocumentTemplateType.BidAdvertisement)
    )
      return null;

    if (documentTypesFilter && !documentTypesFilter.includes(trueDocumentType)) {
      return null;
    }

    if (trueDocumentType === DocumentTemplateType.SubmittalPackages)
      documentType = DocumentTemplateType.Submittals;
    else if (trueDocumentType === DocumentTemplateType.CloseoutSubmittalPackages)
      documentType = DocumentTemplateType.CloseoutSubmittals;
    else if (trueDocumentType === DocumentTemplateType.AsBuiltPackages)
      documentType = DocumentTemplateType.AsBuilt;

    // getPageTitle reads the type off documentTemplate.name, which these rows are not fetched
    // with -- trueDocumentType is that same value, resolved from documentTemplateId above, and is
    // what tells a submittal from a submittal package.
    const entry: DocumentFileTreeDocument = {
      id: document.id,
      title: getDocumentTitleForAssociations(document, trueDocumentType),
      userFriendlyName: getPageTitle(trueDocumentType, {
        ...document,
        documentTemplate: { name: trueDocumentType } as IDocumentTemplate,
      }),
      isPackage: documentType !== trueDocumentType,
      checked: false,
    };

    const index = newDocuments.findIndex((doc) => doc.type === documentType);
    if (index !== -1) newDocuments[index].documents.push(entry);
    else newDocuments.push({ type: documentType, documents: [entry] });
  });

  return newDocuments
    .filter((tree) => tree.documents.length > 0)
    .sort((a, b) => ascendingComparator(a, b, 'type'));
};

export const shouldDisplayUser = (user: IUser, isAdminList: boolean) => {
  if (isAdminList) {
    return true;
  }
  return !user.isDisabled && (!!user.isRegistered || user.companyId === null);
};

export const getProjectUsers = (
  project: { projectUserList?: IProjectUser[] } | undefined | null,
  isAdminList: boolean,
): IProjectUser[] => {
  return (
    project?.projectUserList?.filter((pu) => pu.user && shouldDisplayUser(pu.user, isAdminList)) ||
    []
  );
};

// from https://stackoverflow.com/a/68469827
export function inflateObject<T>(base64str: string) {
  const strData = atob(base64str);

  // Convert binary string to character-number array
  const charData = strData.split('').map((x) => {
    return x.charCodeAt(0);
  });

  // Turn number array into byte-array
  const binData = new Uint8Array(charData);

  return JSON.parse(pako.inflate(binData, { to: 'string' })) as T;
}

export const getTaskPriorityColor = (priority: DocumentPriorityType | string): string => {
  switch (priority) {
    case DocumentPriorityType.High:
      return '#EB2F87';
    case DocumentPriorityType.Medium:
      return '#00C875';
    case DocumentPriorityType.Low:
      return '#996633';
    case DocumentPriorityType.ForInformationOnly:
      return '#A25DDC';
    default:
      return '#C4C4C4';
  }
};

/**
 * Background/foreground colors for a status "pill", covering every status a document row can
 * carry: workflow statuses, submittal action-taken values, punch-list statuses, and the
 * withdrawn/N/A edge cases. Statuses are bucketed by meaning — done (green/blue), in-progress
 * (amber), not-started (gray), negative/terminal (red), resubmitted (dark) — and a
 * For-Information-Only priority always wins (purple), matching the Tasks table. Anything
 * unrecognized falls back to neutral gray so a new status never renders as an invisible pill.
 */
export const getTaskStatusColors = (
  status: string | null | undefined,
  priority?: string | null,
): { bg: string; fg: string } => {
  const GREEN = { bg: '#2BB073', fg: '#fff' };
  const BLUE = { bg: '#0947B9', fg: '#fff' };
  const LIGHT_BLUE = { bg: '#73A3FF', fg: '#fff' };
  const AMBER = { bg: '#FDAB3D', fg: '#fff' };
  const RED = { bg: '#E2445C', fg: '#fff' };
  const DARK = { bg: '#323338', fg: '#fff' };
  const PURPLE = { bg: '#A25DDC', fg: '#fff' };
  const LIGHT_GRAY = { bg: '#C4C4C4', fg: '#323338' };
  const GRAY = { bg: '#9E9E9E', fg: '#323338' };

  if (priority === DocumentPriorityType.ForInformationOnly) return PURPLE;

  switch (status) {
    // Done / accepted.
    case WorkflowStatusType.ReviewComplete:
    case WorkflowStatusType.Completed:
    case WorkflowStatusType.Accepted:
    case ActionTakenType.NoExceptionsTaken:
    case PunchListStatusType.Accepted:
    case 'Approved':
    case 'No Exceptions Taken':
      return GREEN;

    // Resolved / verified.
    case WorkflowStatusType.Resolved:
    case PunchListStatusType.ReadyForVerification:
      return BLUE;

    // In progress / awaiting action.
    case WorkflowStatusType.UnderReview:
    case WorkflowStatusType.ArchitectUploaded:
    case PunchListStatusType.InProgress:
    case ActionTakenType.AmendAsNoted:
    case ActionTakenType.SubmitSpecifiedItem:
    case ActionTakenType.SeeSubmittalComments:
    case ActionTakenType.SeeTransmittalComments:
      return AMBER;

    // Resubmitted.
    case ActionTakenType.ReviseAndResubmit:
    case WorkflowStatusType.Resubmitted:
      return DARK;

    // Negative / terminal.
    case WorkflowStatusType.Canceled:
    case PunchListStatusType.Rejected:
    case ActionTakenType.RemoveFromPackage:
    case ActionTakenType.Withdrawn:
    case 'Rejected':
    case 'Revise & Resubmit':
    case 'withdrawn':
      return RED;

    // For-information-only status value (as opposed to priority, handled above).
    case ActionTakenType.ForInformationOnly:
      return PURPLE;

    case WorkflowStatusType.SubcontractorUploaded:
    case WorkflowStatusType.ReadyForSubmissionToArchitect:
    case WorkflowStatusType.GeneralContractorUploaded:
    case WorkflowStatusType.SubmittedForReview:
      return GRAY;

    // Not started / freshly submitted / uploaded / new — neutral.
    case WorkflowStatusType.Initial:
    case PunchListStatusType.New:
    default:
      return LIGHT_GRAY;
  }
};

/**
 * The status value that actually drives a row's status pill/color bar: the punch list status
 * for punch list items, an action-taken value once a submittal is Completed, otherwise the
 * workflow status. Single source of truth shared by every table/summary that colors rows by
 * status (DocumentIndexRow, DashboardUpcomingList, MyTasksStatusSummary), so they can never
 * disagree with each other.
 */
export const getDocumentStatusKey = (
  doc: {
    workflowStatus?: string | null;
    actionTaken?: string | null;
    punchListStatus?: string | null;
  },
  documentType: DocumentTemplateType | undefined,
): string | null | undefined =>
  documentType === DocumentTemplateType.PunchList
    ? doc.punchListStatus
    : doc.workflowStatus === WorkflowStatusType.Completed && doc.actionTaken
    ? doc.actionTaken
    : doc.workflowStatus;

/**
 * Broad status buckets shared across every document type, keyed by the exact status-pill color
 * from getTaskStatusColors, so a summary mixing multiple document types (e.g. My Work) can
 * group them consistently with how each row's own pill/bar is colored. Order matters for
 * legend/slice display.
 *
 * "Submitted for Review" is listed separately from "Awaiting Submission / Not Started" even
 * though getTaskStatusColors colors them the same (both gray) — getStatusBucketLabel below splits
 * them apart by document type before falling back to this color table, since
 * WorkflowStatusType.SubmittedForReview means "not started yet" for Task/CA Task but genuinely
 * "submitted, awaiting review" for every other document type.
 */
export const STATUS_BUCKET_LABELS: { color: string; label: string }[] = [
  { color: '#C4C4C4', label: 'Awaiting Submission' },
  { color: '#9E9E9E', label: 'Submitted for Review' },
  { color: '#FDAB3D', label: 'Under Review' },
  { color: '#73A3FF', label: 'Review Complete' },
  { color: '#2BB073', label: 'Completed' },
  { color: '#0947B9', label: 'Resolved' },
  { color: '#323338', label: 'Resubmitted' },
  { color: '#E2445C', label: 'Rejected' },
  { color: '#A25DDC', label: 'For Information Only' },
];

/**
 * The status bucket label (a STATUS_BUCKET_LABELS entry) a document falls into. Splits
 * WorkflowStatusType.SubmittedForReview by document type before falling back to the generic
 * color-keyed lookup in STATUS_BUCKET_LABELS — see that constant's comment for why.
 */
export const getStatusBucketLabel = (
  doc: {
    workflowStatus?: string | null;
    actionTaken?: string | null;
    punchListStatus?: string | null;
    priority?: string | null;
  },
  documentType: DocumentTemplateType | undefined,
): string | undefined => {
  const statusKey = getDocumentStatusKey(doc, documentType);
  const { bg } = getTaskStatusColors(statusKey, doc.priority);
  return STATUS_BUCKET_LABELS.find((b) => b.color === bg)?.label;
};

/**
 * Counts a mixed-document-type list into STATUS_BUCKET_LABELS buckets plus a separate overdue
 * count, single source of truth for both the My Work status summary's display and the My Work
 * filter dialog's dynamically-populated status options, so they can't disagree about what's
 * actually present in the list.
 */
export const computeStatusBucketCounts = (
  documents: INumberedDocumentView[],
  documentTemplateIdToName: Record<string, DocumentTemplateType>,
): { counts: Record<string, number>; overdueCount: number } => {
  const counts: Record<string, number> = {};
  STATUS_BUCKET_LABELS.forEach(({ label }) => {
    counts[label] = 0;
  });
  let overdueCount = 0;
  documents.forEach((doc) => {
    const documentType = documentTemplateIdToName[doc.documentTemplateId];
    const label = getStatusBucketLabel(doc, documentType);
    if (label) counts[label] += 1;
    if (documentIsOverdue(doc)) overdueCount += 1;
  });
  return { counts, overdueCount };
};

export const getUserFriendlyDocumentPriority = (priority: DocumentPriorityType) => {
  switch (priority) {
    case DocumentPriorityType.Low:
      return 'Low';
    case DocumentPriorityType.Medium:
      return 'Medium';
    case DocumentPriorityType.High:
      return 'High';
    case DocumentPriorityType.ForInformationOnly:
      return 'For Information Only';
    default:
      return priority;
  }
};

export const getUserFriendlyTaskStatusFromWorkflowStatus = (
  status: WorkflowStatusType | null | undefined,
) => {
  switch (status) {
    case WorkflowStatusType.SubmittedForReview:
      return 'Not Started';
    case WorkflowStatusType.ArchitectUploaded:
    case WorkflowStatusType.UnderReview:
      return 'In Progress';
    case WorkflowStatusType.ReviewComplete:
      return 'Completed';
    case WorkflowStatusType.Resolved:
      return 'Accepted';
    case WorkflowStatusType.Resubmitted:
      return 'Resubmitted';
    default:
      return getUserFacingWorkflowStatus(status);
  }
};

export const getUserFriendlyCost = (cost: number | null) => {
  if (cost == null) return null;
  const dollars = cost / 100;
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export const getUserFriendlyNumber = (num: number | null) => {
  if (num == null) return null;
  return num.toString();
};

export const getReferenceNumberLabelFromDocumentType = (
  documentType: DocumentTemplateType | null,
) => {
  switch (documentType) {
    case DocumentTemplateType.Task:
      return 'Design Task #';

    case DocumentTemplateType.CaTask:
      return 'CA Task #';

    case DocumentTemplateType.AsiDocuments:
      return 'ASI #';

    case DocumentTemplateType.RequestsForInformation:
      return 'RFI #';

    case DocumentTemplateType.ChangeOrders:
      return 'Change Order #';

    case DocumentTemplateType.ConstructionChangeDirectives:
      return 'CCD #';

    case DocumentTemplateType.PotentialChangeOrders:
      return 'PCO #';

    case DocumentTemplateType.RequestsForChange:
      return 'RFC #';

    case DocumentTemplateType.WorkChangeProposalRequests:
      return 'WCPR #';

    case DocumentTemplateType.PayApplications:
      return 'Pay App #';

    case DocumentTemplateType.Testing:
      return 'Testing #';

    case DocumentTemplateType.MiscellaneousDocuments:
      return 'Misc. Document #';

    case DocumentTemplateType.Schedules:
      return 'Schedule #';

    case DocumentTemplateType.Addenda:
      return 'Addendum #';

    case DocumentTemplateType.InformationalItems:
      return 'Informational Item #';

    case DocumentTemplateType.WarrantyItems:
      return 'Reference #';

    case DocumentTemplateType.DesignPackages:
      return 'Item #';

    default:
      return 'Document #';
  }
};

export const getUserFriendlyUser = (user: IUser | null) => {
  if (user == null) return null;
  if (user.company) return `${user.name} from ${user.company.name}`;
  return user.name;
};

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function stringIsUuid(str: string) {
  return uuidRegex.test(str);
}

export const getUserFriendlyDocumentFields = (
  patch: Partial<INumberedDocumentView> & { contactPhone?: string | null },
  documentType: DocumentTemplateType | null,
) => {
  const userFriendlyKeyValues: { key: string; value: string | null }[] = [];
  if (patch.contactPhone !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('contactPhone'),
      value: patch.contactPhone,
    });
  if (patch.documentTemplateId !== undefined)
    userFriendlyKeyValues.push({
      key: 'Document Type',
      value: patch.documentTemplateId,
    });
  if (patch.documentTemplate !== undefined) {
    userFriendlyKeyValues.push({
      key: 'Document Type',
      value:
        patch.documentTemplate !== null
          ? getUserFriendlyDocumentTemplateNameSingular(
              patch.documentTemplate.name as DocumentTemplateType,
            )
          : null,
    });
  }
  if (patch.projectId !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('projectId'), value: patch.projectId });
  if (patch.isHidden !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('isHidden'),
      value: patch.isHidden ? 'True' : 'False',
    });
  if (patch.assignedToUserId !== undefined)
    userFriendlyKeyValues.push({
      key: 'Currently Responsible',
      value: patch.assignedToUserId,
    });
  if (patch.workflowStatus !== undefined)
    userFriendlyKeyValues.push({
      key: 'Current Status',
      value: patch.workflowStatus,
    });
  if (patch.architectUserId !== undefined)
    userFriendlyKeyValues.push({
      key: 'Responsible Architect',
      value: patch.architectUserId,
    });
  if (patch.architectUser !== undefined)
    userFriendlyKeyValues.push({
      key: 'Architect',
      value: getUserFriendlyUser(patch.architectUser),
    });
  if (patch.generalContractorUserId !== undefined)
    userFriendlyKeyValues.push({
      key: 'Responsible General Contractor',
      value: patch.generalContractorUserId,
    });
  if (patch.generalContractorUser !== undefined)
    userFriendlyKeyValues.push({
      key: 'General Contractor',
      value: getUserFriendlyUser(patch.generalContractorUser),
    });
  if (patch.submittalSection !== undefined)
    userFriendlyKeyValues.push({
      key: 'Specification Section',
      value: patch.submittalSection,
    });
  if (patch.submittalParagraphNumber !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('submittalParagraphNumber'),
      value: patch.submittalParagraphNumber,
    });
  if (patch.submittalSubparagraphNumber !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('submittalSubparagraphNumber'),
      value: patch.submittalSubparagraphNumber,
    });
  if (patch.submittalSectionDescription !== undefined)
    userFriendlyKeyValues.push({
      key: 'Section Description',
      value: patch.submittalSectionDescription,
    });
  if (patch.title !== undefined)
    userFriendlyKeyValues.push({
      key:
        documentType === DocumentTemplateType.Submittals ||
        documentType === DocumentTemplateType.CloseoutSubmittals
          ? 'Description'
          : _.startCase('title'),
      value: patch.title,
    });
  if (patch.description !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('description'), value: patch.description });
  if (patch.referenceNumber !== undefined)
    userFriendlyKeyValues.push({
      key: getReferenceNumberLabelFromDocumentType(documentType),
      value: patch.referenceNumber,
    });
  if (patch.documentNumber !== undefined)
    userFriendlyKeyValues.push({
      key: getReferenceNumberLabelFromDocumentType(documentType),
      value:
        (documentType === DocumentTemplateType.Addenda ||
        documentType === DocumentTemplateType.InformationalItems
          ? patch.documentNumber?.toString().padStart(2, '0')
          : patch.documentNumber?.toString()) || null,
    });
  if (patch.cost !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('cost'),
      value: getUserFriendlyCost(patch.cost),
    });
  if (patch.approvedCost !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('approvedCost'),
      value: getUserFriendlyCost(patch.approvedCost),
    });
  if (patch.requestedAdditionalDays !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('requestedAdditionalDays'),
      value: getUserFriendlyNumber(patch.requestedAdditionalDays),
    });
  if (patch.impacts !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('impacts'), value: patch.impacts });
  if (patch.proposedAction !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('proposedAction'),
      value: patch.proposedAction,
    });
  if (patch.proposedCostImpact !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('proposedCostImpact'),
      value: getUserFriendlyCost(patch.proposedCostImpact) || 'TBD',
    });
  if (patch.approvedCostImpact !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('approvedCostImpact'),
      value: getUserFriendlyCost(patch.approvedCostImpact),
    });
  if (patch.proposedTimeImpact !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('proposedTimeImpact'),
      value:
        patch.proposedTimeImpact != null
          ? `${getUserFriendlyNumber(patch.proposedTimeImpact)} day(s)`
          : 'TBD',
    });
  if (patch.approvedTimeImpact !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('approvedTimeImpact'),
      value:
        patch.approvedTimeImpact != null
          ? `${getUserFriendlyNumber(patch.approvedTimeImpact)} day(s)`
          : null,
    });
  if (patch.reportedBy !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('reportedBy'), value: patch.reportedBy });
  if (patch.ballInCourt !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('ballInCourt'), value: patch.ballInCourt });
  if (patch.subcontractorUserId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('subcontractorUserId'),
      value: patch.subcontractorUserId,
    });
  if (patch.subcontractorUser !== undefined)
    userFriendlyKeyValues.push({
      key: 'Subcontractor',
      value: getUserFriendlyUser(patch.subcontractorUser),
    });
  if (patch.dateReceived !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateReceived'),
      value: patch.dateReceived,
    });
  if (patch.dateSentOut !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('dateSentOut'), value: patch.dateSentOut });
  if (patch.changeOrdersValue !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('changeOrdersValue'),
      value: getUserFriendlyCost(patch.changeOrdersValue),
    });
  if (patch.changeOrdersDaysAdded !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('changeOrdersDaysAdded'),
      value: getUserFriendlyNumber(patch.changeOrdersDaysAdded),
    });
  if (patch.receivedFrom !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('receivedFrom'),
      value: patch.receivedFrom,
    });
  if (patch.typeOfTesting !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('typeOfTesting'),
      value: patch.typeOfTesting,
    });
  if (patch.typeOfTestingOther !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('typeOfTestingOther'),
      value: patch.typeOfTestingOther,
    });
  if (patch.actionTaken !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('actionTaken'), value: patch.actionTaken });
  if (patch.recommendedAction !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('recommendedAction'),
      value: patch.recommendedAction,
    });
  if (patch.revisionNumber !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('revisionNumber'),
      value: getUserFriendlyNumber(patch.revisionNumber),
    });
  if (patch.request !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('request'), value: patch.request });

  if (patch.anticipatedInitialSubmissionDate !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('anticipatedInitialSubmissionDate'),
      value: patch.anticipatedInitialSubmissionDate,
    });
  if (patch.actualSubmissionDate !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('actualSubmissionDate'),
      value: patch.actualSubmissionDate,
    });
  if (patch.dateReturnedToContractor !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateReturnedToContractor'),
      value: patch.dateReturnedToContractor,
    });

  if (patch.additionalReviewRecommendedById !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('additionalReviewRecommendedById'),
      value: patch.additionalReviewRecommendedById,
    });
  if (patch.reviewCompleteOn !== undefined)
    userFriendlyKeyValues.push({
      key:
        documentType !== DocumentTemplateType.WarrantyItems
          ? _.startCase('reviewCompleteOn')
          : 'Completed Date',
      value: patch.reviewCompleteOn,
    });
  if (patch.additionalReviewForDocumentId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('additionalReviewForDocumentId'),
      value: patch.additionalReviewForDocumentId,
    });
  if (patch.pageNumber !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('pageNumber'),
      value: getUserFriendlyNumber(patch.pageNumber),
    });
  if (patch.paragraphNumber !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('paragraphNumber'),
      value: getUserFriendlyNumber(patch.paragraphNumber),
    });
  if (patch.proposedSubstitution !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('proposedSubstitution'),
      value: patch.proposedSubstitution,
    });
  if (patch.substitutionReason !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('substitutionReason'),
      value: patch.substitutionReason,
    });
  if (patch.substitutionAffectsDrawing !== undefined)
    userFriendlyKeyValues.push({
      key: 'Substitution Effect on Drawings',
      value: patch.substitutionAffectsDrawing,
    });
  if (patch.substitutionAffectsTrades !== undefined)
    userFriendlyKeyValues.push({
      key: 'Substitution Effect on Trades',
      value: patch.substitutionAffectsTrades,
    });
  if (patch.substitutionAffectedBy !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('substitutionAffectedBy'),
      value: patch.substitutionAffectedBy,
    });
  if (patch.substitutionDifferences !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('substitutionDifferences'),
      value: patch.substitutionDifferences,
    });
  if (patch.manufacturersWarranties !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('manufacturersWarranties'),
      value: patch.manufacturersWarranties,
    });
  if (patch.manufacturersWarrantiesExplanation !== undefined)
    userFriendlyKeyValues.push({
      key: 'Warranty Explanation',
      value: patch.manufacturersWarrantiesExplanation,
    });
  if (patch.informationAvailable !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('informationAvailable'),
      value: patch.informationAvailable,
    });
  if (patch.submittalPackageDocumentId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('submittalPackageDocumentId'),
      value: patch.submittalPackageDocumentId,
    });
  if (patch.submittalSectionFileId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('submittalSectionFileId'),
      value: patch.submittalSectionFileId,
    });
  if (patch.drawingsSectionFileId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('drawingsSectionFileId'),
      value: patch.drawingsSectionFileId,
    });
  if (patch.completedOn !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('completedOn'), value: patch.completedOn });
  if (patch.subcontractorAssignedOn !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('subcontractorAssignedOn'),
      value: patch.subcontractorAssignedOn,
    });
  if (patch.submittalUploadedOn !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('submittalUploadedOn'),
      value: patch.submittalUploadedOn,
    });
  if (patch.numberOfSheets !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('numberOfSheets'),
      value: getUserFriendlyNumber(patch.numberOfSheets),
    });
  if (patch.shouldSyncProcore !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('shouldSyncProcore'),
      value: patch.shouldSyncProcore ? 'Yes' : 'No',
    });
  if (patch.dateApproved !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateApproved'),
      value: patch.dateApproved,
    });
  if (patch.agency !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('agency'), value: patch.agency });
  if (patch.location !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('location'), value: patch.location });
  if (patch.dateContractorCompleted !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateContractorCompleted'),
      value: patch.dateContractorCompleted,
    });
  if (patch.licenseNumber !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('licenseNumber'),
      value: patch.licenseNumber,
    });
  if (patch.contactName !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('contactName'), value: patch.contactName });
  if (patch.contactCompany !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('contactCompany'),
      value: patch.contactCompany,
    });
  if (patch.isFinal !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('isFinal'),
      value: patch.isFinal ? 'Yes' : 'No',
    });
  if (patch.dateReturnedToReviewer !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateReturnedToReviewer'),
      value: patch.dateReturnedToReviewer,
    });
  if (patch.addendumNumber !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('addendumNumber'),
      value: patch.addendumNumber,
    });
  if (patch.isDraft !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('isDraft'),
      value: patch.isDraft ? 'Yes' : 'No',
    });
  if (patch.weather !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('weather'), value: patch.weather });
  if (patch.partiesPresent !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('partiesPresent'),
      value: patch.partiesPresent ? patch.partiesPresent.join(', ') : 'None',
    });
  if (patch.customArchitect !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('customArchitect'),
      value: patch.customArchitect,
    });
  if (patch.customContractor !== undefined)
    userFriendlyKeyValues.push({
      key: 'Responsible Subcontractor',
      value: patch.customContractor || 'None',
    });
  if (patch.currentWorkInProgress !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('currentWorkInProgress'),
      value: patch.currentWorkInProgress,
    });
  if (patch.reviewComment !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('reviewComment'),
      value: patch.reviewComment,
    });
  if (patch.dateOfObservation !== undefined)
    userFriendlyKeyValues.push({
      key:
        documentType === DocumentTemplateType.MeetingMinutes ||
        documentType === DocumentTemplateType.DesignMeetingMinutes
          ? 'Date Observed'
          : documentType === DocumentTemplateType.ContractorDailyLogs
          ? 'Log Date'
          : 'Date of Observation',
      value: patch.dateOfObservation,
    });
  if (patch.parsedFromFileId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('parsedFromFileId'),
      value: patch.parsedFromFileId,
    });
  if (patch.procoreId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('procoreId'),
      value: getUserFriendlyNumber(patch.procoreId),
    });
  if (patch.sheetNumber !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('sheetNumber'), value: patch.sheetNumber });
  if (patch.sheetNumberConfidence !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('sheetNumberConfidence'),
      value: getUserFriendlyNumber(patch.sheetNumberConfidence),
    });
  if (patch.isFileLocked !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('isFileLocked'),
      value: patch.isFileLocked ? 'Yes' : 'No',
    });
  if (patch.additionalReviewForPackageDocumentId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('additionalReviewForPackageDocumentId'),
      value: patch.additionalReviewForPackageDocumentId,
    });
  if (patch.isProcoreOriginated !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('isProcoreOriginated'),
      value: patch.isProcoreOriginated ? 'Yes' : 'No',
    });
  if (patch.isResponseOnProcore !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('isResponseOnProcore'),
      value: patch.isResponseOnProcore ? 'Yes' : 'No',
    });
  if (patch.procoreDocumentType !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('procoreDocumentType'),
      value: patch.procoreDocumentType ? _.startCase(patch.procoreDocumentType) : null,
    });
  if (patch.punchListStatus !== undefined)
    userFriendlyKeyValues.push({
      key: 'Current Status',
      value: patch.punchListStatus,
    });
  if (patch.physicalLocationId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('physicalLocationId'),
      value: patch.physicalLocationId,
    });
  if (patch.markedLocation !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('markedLocation'),
      value: '',
    });
  if (patch.parsedParentDocumentId !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('parsedParentDocumentId'),
      value: patch.parsedParentDocumentId,
    });
  if (patch.willPublishResponse !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('willPublishResponse'),
      value: patch.willPublishResponse ? 'Yes' : 'No',
    });
  if (patch.datePublished !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('datePublished'),
      value: patch.datePublished,
    });
  if (patch.dateFinalExecuted !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateFinalExecuted'),
      value: patch.dateFinalExecuted,
    });
  if (patch.dateOfLastUpdate !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateOfLastUpdate'),
      value: patch.dateOfLastUpdate,
    });
  if (patch.priority !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('priority'), value: patch.priority });
  if (patch.simplePackage !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('simplePackage'),
      value: patch.simplePackage,
    });
  if (patch.expectedCompletionDate !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('expectedCompletionDate'),
      value: patch.expectedCompletionDate,
    });
  if (patch.dateSubmitted !== undefined)
    userFriendlyKeyValues.push({
      key: _.startCase('dateSubmitted'),
      value: patch.dateSubmitted,
    });
  if (patch.createdOn !== undefined)
    userFriendlyKeyValues.push({
      key: 'Created On',
      value: patch.createdOn,
    });
  if (patch.dueDate !== undefined)
    userFriendlyKeyValues.push({ key: _.startCase('dueDate'), value: patch.dueDate });
  if (patch.substantialCompletionDate !== undefined) {
    userFriendlyKeyValues.push({
      key: 'Substantial Completion Date',
      value: patch.substantialCompletionDate,
    });
  }

  return userFriendlyKeyValues;
};

export async function canvasToBlobAsync(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
    });
  });
}

interface QRCodeOptions {
  link: string;
  width?: number;
  height?: number;
  quietZone?: number;
  titleHeight?: number;
  title?: string;
  titleTop?: number;
  subTitle?: string;
  subTitleTop?: number;
  onRenderingEnd?: (options: any, url: string) => void;
  qrcodeRef: HTMLElement;
  drawer: 'canvas' | 'svg';
}

export const createQRCode = (options: QRCodeOptions) => {
  const {
    link,
    width = 224,
    height = 224,
    quietZone = 0,
    titleHeight,
    title,
    titleTop = 8,
    subTitle,
    subTitleTop = 26,
    onRenderingEnd,
    qrcodeRef,
    drawer,
  } = options;

  let trueTitleHeight = titleHeight;
  if (trueTitleHeight === undefined) {
    trueTitleHeight = 0;
    if (title) trueTitleHeight += 16;
    if (subTitle) trueTitleHeight += 16;
  }

  let subTitleFormatted = subTitle;
  if (subTitleFormatted && subTitleFormatted.length > 38) {
    subTitleFormatted = subTitle?.substring(0, 35) + '...';
  }

  new QRCode(qrcodeRef, {
    text: link,
    logo: qrcodeLogo,
    logoBackgroundColor: '#0947B9',
    width,
    height,
    correctLevel: QRCode.CorrectLevel.M,
    drawer,
    quietZone,
    onRenderingEnd,
    titleHeight: trueTitleHeight,
    title,
    titleTop,
    subTitle: subTitleFormatted,
    subTitleTop,
  });
};

export const getRelevantFieldsByDocumentType = (
  documentType: DocumentTemplateType,
): (keyof INumberedDocumentView)[] => {
  switch (documentType) {
    case DocumentTemplateType.Drawings:
      return ['title', 'sheetNumber'];

    case DocumentTemplateType.Specifications:
      return ['submittalSection', 'submittalSectionDescription'];

    case DocumentTemplateType.CloseoutSubmittals:
    case DocumentTemplateType.Submittals:
      return [
        'assignedToUserId',
        'workflowStatus',
        'generalContractorUserId',
        'architectUserId',
        'submittalSection',
        'submittalSectionDescription',
        'title',
        'actualSubmissionDate',
        'dueDate',
      ];

    case DocumentTemplateType.RequestsForInformation:
    case DocumentTemplateType.WorkChangeProposalRequests:
      return [
        'assignedToUserId',
        'description',
        'workflowStatus',
        'generalContractorUserId',
        'architectUserId',
        'title',
        'referenceNumber',
        'dateSubmitted',
        'dueDate',
      ];

    case DocumentTemplateType.ChangeOrders:
      return [
        'title',
        'description',
        'referenceNumber',
        'changeOrdersValue',
        'changeOrdersDaysAdded',
      ];

    case DocumentTemplateType.RequestsForChange:
    case DocumentTemplateType.PotentialChangeOrders:
      return [
        'referenceNumber',
        'title',
        'description',
        'workflowStatus',
        'assignedToUserId',
        'architectUserId',
        'generalContractorUserId',
        'proposedCostImpact',
        'proposedTimeImpact',
        'dateSubmitted',
        'dueDate',
      ];

    case DocumentTemplateType.PayApplications:
      return [
        'assignedToUserId',
        'description',
        'workflowStatus',
        'generalContractorUserId',
        'architectUserId',
        'title',
        'referenceNumber',
        'dateSubmitted',
        'dueDate',
        'isFinal',
        'dateFinalExecuted',
      ];

    // description and currentWorkInProgress are listed separately on the export page
    case DocumentTemplateType.FieldReports:
      return [
        'location',
        'weather',
        'contactCompany',
        'dateOfObservation',
        // 'description',
        // 'currentWorkInProgress',
      ];

    case DocumentTemplateType.ContractorDailyLogs:
    case DocumentTemplateType.MeetingMinutes:
    case DocumentTemplateType.DesignMeetingMinutes:
      return ['title', 'description', 'dateOfObservation'];

    case DocumentTemplateType.Schedules:
      return ['title', 'description', 'documentNumber'];

    case DocumentTemplateType.MiscellaneousDocuments:
      return ['title', 'description', 'referenceNumber'];

    case DocumentTemplateType.Testing:
      return ['referenceNumber', 'title', 'description', 'agency'];

    case DocumentTemplateType.InformationalItems:
    case DocumentTemplateType.Addenda:
      return ['title', 'documentNumber'];

    case DocumentTemplateType.BidderRfIs:
      return ['workflowStatus', 'description', 'dateSubmitted', 'dueDate'];

    case DocumentTemplateType.SubstitutionRequests:
      return [
        'workflowStatus',
        'submittalSection',
        'pageNumber',
        'paragraphNumber',
        'description',
        'proposedSubstitution',
        'substitutionReason',
        'substitutionAffectsDrawing',
        'substitutionAffectsTrades',
        'substitutionDifferences',
        'manufacturersWarrantiesExplanation',
        'informationAvailable',
        'agency',
        'dateSubmitted',
        'dueDate',
      ];

    case DocumentTemplateType.WarrantyItems:
      return [
        'description',
        'referenceNumber',
        'location',
        'customContractor',
        'expectedCompletionDate',
        'punchListStatus',
        'reviewCompleteOn',
      ];

    default:
      return ['title', 'description', 'referenceNumber'];
  }
};

/**
 * Task's "-00"/"-01" task/subtask suffix, read off the (reused) submittalParagraphNumber
 * field. Empty for legacy Task documents created before this field was populated.
 */
export const getTaskNumberSuffix = (document: INumberedDocumentView): string =>
  document.submittalParagraphNumber ? `-${document.submittalParagraphNumber}` : '';

export const getDocumentIdentifier = (
  document: INumberedDocumentView | null,
  documentType: DocumentTemplateType | null,
  options: { includeSubmittalTitle?: boolean; isPackage?: boolean; onlyNumber?: boolean } = {},
): string => {
  const { includeSubmittalTitle = false, isPackage = false, onlyNumber = false } = options;

  if (!document || !documentType) return '';

  switch (documentType) {
    case DocumentTemplateType.Specifications: {
      if (onlyNumber) {
        return document.submittalSection || '';
      }
      return `${document.submittalSection} ${document.submittalSectionDescription}`;
    }
    case DocumentTemplateType.CloseoutSubmittals:
    case DocumentTemplateType.Submittals: {
      if (isPackage) {
        return document.referenceNumber!;
      }
      let identifier = document.submittalSection || '';
      if (document.submittalParagraphNumber) identifier += `-${document.submittalParagraphNumber}`;
      if (document.submittalSubparagraphNumber)
        identifier += `-${document.submittalSubparagraphNumber}`;
      if (document.revisionNumber) identifier += `-R${document.revisionNumber}`;
      if (onlyNumber) return identifier;
      if (includeSubmittalTitle) {
        if (document.submittalSectionDescription)
          identifier += ` ${document.submittalSectionDescription}`;
        if (document.title) identifier += ` — ${document.title}`;
      }
      return identifier;
    }

    case DocumentTemplateType.ContractorDailyLogs:
    case DocumentTemplateType.MeetingMinutes:
    case DocumentTemplateType.DesignMeetingMinutes:
    case DocumentTemplateType.FieldReports: {
      return getDocumentNameDate(document);
    }

    case DocumentTemplateType.InformationalItems:
    case DocumentTemplateType.Addenda: {
      const number = getDocumentNumberText(document, documentType);
      if (onlyNumber) return number;
      return `${number ? `#${number}` : ''}${document.title ? ` — ${document.title}` : ''}`;
    }

    case DocumentTemplateType.AsBuilt:
    case DocumentTemplateType.Drawings: {
      return `#${document.sheetNumber}${
        document.revisionNumber ? `-R${document.revisionNumber}` : ''
      }`;
    }

    case DocumentTemplateType.RequestsForInformation:
    case DocumentTemplateType.Task:
    case DocumentTemplateType.CaTask: {
      const number = getDocumentNumberText(document, documentType);
      const numbered = number ? `#${number}` : '';
      if (onlyNumber) return numbered;
      return `${numbered}${document.title ? ` — ${document.title}` : ''}`;
    }

    case DocumentTemplateType.WarrantyItems: {
      return `#${getDocumentNumberText(document, documentType)}`;
    }

    default: {
      return `#${document?.referenceNumber || document.documentNumber}${
        document.revisionNumber ? `-R${document.revisionNumber}` : ''
      }`;
    }
  }
};

export const getUserFriendlyConsultantResponse = (
  response?: ActionTakenType | null,
  labelOverrides?: ActionTakenLabelOverrides,
  documentType?: DocumentTemplateType | null,
) => {
  if (response) {
    const custom = getCustomActionTakenLabel(response, labelOverrides, documentType);
    if (custom) return custom;
  }
  switch (response) {
    case ActionTakenType.SubmitSpecifiedItem:
      return 'Submit Specified Item';

    case ActionTakenType.ReviseAndResubmit:
      return 'Revise & Resubmit';

    case ActionTakenType.NoExceptionsTaken:
      return 'No Exceptions Taken';

    case ActionTakenType.AmendAsNoted:
      return 'Amend As Noted';

    case ActionTakenType.SeeSubmittalComments:
      return 'See Submittal Comments';

    case ActionTakenType.SeeTransmittalComments:
      return 'See Transmittal Comments';

    case ActionTakenType.RemoveFromPackage:
      return 'Remove from Package';

    case ActionTakenType.Rejected:
      return 'Rejected';

    default:
      return 'N/A';
  }
};

const emailStatusLabels: { [K in EmailStatus]: string } = {
  [EmailStatus.WillNotSend]: 'Disabled by Preference',
  [EmailStatus.UnsentWaiting]: 'Pending',
  [EmailStatus.Sending]: 'Sent',
  [EmailStatus.Delivery]: 'Delivered',
  [EmailStatus.Bounce]: 'Delivery Exception',
  [EmailStatus.Complaint]: 'Delivery Exception',
  [EmailStatus.OtherFailure]: 'Delivery Exception',
};
export const getUserFacingEmailStatus = (status: EmailStatus): string => emailStatusLabels[status];

const emailStatusColors: { [K in EmailStatus]: string } = {
  [EmailStatus.WillNotSend]: '#128750',
  [EmailStatus.UnsentWaiting]: '#0947B9',
  [EmailStatus.Sending]: '#0947B9',
  [EmailStatus.Delivery]: '#128750',
  [EmailStatus.Bounce]: '#ED3F26',
  [EmailStatus.Complaint]: '#ED3F26',
  [EmailStatus.OtherFailure]: '#ED3F26',
};
export const getColorFromEmailStatus = (status: EmailStatus): string => emailStatusColors[status];

export type FileDataUrl = ArrayBuffer | string | null;

export interface FileWithData {
  file: File;
  data: FileDataUrl;
}

export const readFileAsync = (file: FileWithPath): Promise<FileDataUrl> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const docTypesEnableStudioLink = [
  DocumentTemplateType.Submittals,
  DocumentTemplateType.CloseoutSubmittals,
  DocumentTemplateType.SubmittalPackages,
  DocumentTemplateType.CloseoutSubmittalPackages,
  DocumentTemplateType.RequestsForInformation,
  DocumentTemplateType.RequestsForChange,
  DocumentTemplateType.WorkChangeProposalRequests,
  DocumentTemplateType.PotentialChangeOrders,
  DocumentTemplateType.Task,
  DocumentTemplateType.CaTask,
  DocumentTemplateType.DesignPackages,
];

export const isUserInGroup = (userGroup: IUserGroup, userId: string) => {
  return !!userGroup.userGroupUserList?.some((userGroupUser) => userGroupUser.userId === userId);
};

export const convertToBase64 = (file: File | FileWithPath) => {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result as string);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const resizeImage = (base64image: string, width: number, height: number) => {
  return new Promise<string>((resolve) => {
    const img = new Image();
    img.src = base64image;

    img.onload = () => {
      // Check if the image require resize at all
      if (img.height <= height && img.width <= width) {
        resolve(base64image);
      } else {
        // Make sure the width and height preserve the original aspect ratio and adjust if needed
        if (img.height > img.width) {
          width = Math.floor(height * (img.width / img.height));
        } else {
          height = Math.floor(width * (img.height / img.width));
        }

        const resizingCanvas: HTMLCanvasElement = document.createElement('canvas');
        const resizingCanvasContext = resizingCanvas.getContext('2d');

        // Start with original image size
        resizingCanvas.width = img.width;
        resizingCanvas.height = img.height;

        // Draw the original image on the (temp) resizing canvas
        resizingCanvasContext?.drawImage(img, 0, 0, resizingCanvas.width, resizingCanvas.height);

        const curImageDimensions = {
          width: Math.floor(img.width),
          height: Math.floor(img.height),
        };

        const halfImageDimensions: { width: number | null; height: number | null } = {
          width: null,
          height: null,
        };

        // Quickly reduce the dize by 50% each time in few itterations until the size is less then
        // 2x time the target size - the motivation for it, is to reduce the aliasing that would have been
        // created with direct reduction of very big image to small image
        while (curImageDimensions.width * 0.5 > width) {
          // Reduce the resizing canvas by half and refresh the image
          halfImageDimensions.width = Math.floor(curImageDimensions.width * 0.5);
          halfImageDimensions.height = Math.floor(curImageDimensions.height * 0.5);

          resizingCanvasContext?.drawImage(
            resizingCanvas,
            0,
            0,
            curImageDimensions.width,
            curImageDimensions.height,
            0,
            0,
            halfImageDimensions.width,
            halfImageDimensions.height,
          );

          curImageDimensions.width = halfImageDimensions.width;
          curImageDimensions.height = halfImageDimensions.height;
        }

        // Now do final resize for the resizingCanvas to meet the dimension requirments
        // directly to the output canvas, that will output the final image
        const outputCanvas: HTMLCanvasElement = document.createElement('canvas');
        const outputCanvasContext = outputCanvas.getContext('2d');

        outputCanvas.width = width;
        outputCanvas.height = height;

        outputCanvasContext?.drawImage(
          resizingCanvas,
          0,
          0,
          curImageDimensions.width,
          curImageDimensions.height,
          0,
          0,
          width,
          height,
        );

        // output the canvas pixels as an image. params: format, quality
        resolve(outputCanvas.toDataURL('image/jpeg', 0.9));
      }
    };
  });
};

export function getFileFromBase64(base64String: string, fileName: string) {
  const trimmedString = base64String.replace(/data:image\/(?:jpeg|png);base64,/, '');
  const imageContent = atob(trimmedString);
  const buffer = new ArrayBuffer(imageContent.length);
  const view = new Uint8Array(buffer);

  for (let n = 0; n < imageContent.length; n++) {
    view[n] = imageContent.charCodeAt(n);
  }
  const type = 'image/jpeg';
  const blob = new Blob([buffer], { type });
  return new File([blob], fileName, { lastModified: new Date().getTime(), type });
}

export const resizeImageHelper = async (
  image: File | FileWithPath,
  width: number = 600,
  height: number = 600,
) => {
  const base64image = await convertToBase64(image);
  const resizedBase64image = await resizeImage(base64image, width, height);
  return getFileFromBase64(resizedBase64image, image.name);
};

export const isSurface = () => {
  const isWindows = navigator.userAgent.indexOf('Windows') > -1;
  // @ts-ignore
  const maxTouchPoints = navigator.maxTouchPoints || navigator.msMaxTouchPoints;
  const isTouchable =
    'ontouchstart' in window ||
    maxTouchPoints > 0 ||
    // @ts-ignore
    (window.matchMedia && matchMedia('(any-pointer: coarse)').matches);

  return isWindows && isTouchable;
};

export function updateObjectInStateArray<T extends { id: string }>(
  id: string,
  setState: React.Dispatch<React.SetStateAction<T[]>>,
  patch: Partial<T>,
) {
  setState((prevState) => {
    const index = prevState.findIndex((x) => x.id === id);
    if (index !== -1) {
      const object = prevState.find((x) => x.id === id)!;
      return [...prevState.slice(0, index), { ...object, ...patch }, ...prevState.slice(index + 1)];
    }
    return prevState;
  });
}

export function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

type IframeHighlightOptions = {
  iframe: HTMLIFrameElement;
  query: string;
  wholeWord?: boolean;
  caseSensitive?: boolean;
  ignoreTags?: string[];
  highlightClassName?: string;
  debug?: boolean;
  /** Optional selector inside iframe to limit scope; defaults to body */
  rootSelector?: string;
};

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getIframeDocument(iframe: HTMLIFrameElement): Document | null {
  return iframe.contentDocument ?? iframe.contentWindow?.document ?? null;
}

export function clearDomHighlights(root: HTMLElement, className = 'dom-highlight', debug = false) {
  const marks = root.querySelectorAll(`mark.${className}`);
  if (debug) console.log('[iframe-highlight] clearing marks:', marks.length);

  marks.forEach((m) => {
    const parent = m.parentNode;
    if (!parent) return;
    parent.replaceChild(document.createTextNode(m.textContent ?? ''), m);
    parent.normalize();
  });
}

export const DEFAULT_IGNORE_TAGS = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA'];

export function highlightDom(
  roots: (Element | null)[],
  query: string,
  {
    wholeWord = false,
    caseSensitive = false,
    ignoreTags = DEFAULT_IGNORE_TAGS,
    highlightClassName = 'dom-highlight',
    debug = false,
  }: {
    wholeWord?: boolean;
    caseSensitive?: boolean;
    ignoreTags?: string[];
    highlightClassName?: string;
    debug?: boolean;
  } = {},
) {
  const q = query.trim();
  if (!q) {
    if (debug) console.warn('[iframe-highlight] empty query; abort');
    return;
  }

  const flags = caseSensitive ? 'g' : 'gi';
  const pattern = wholeWord ? `\\b${escapeRegExp(q)}\\b` : escapeRegExp(q);

  let re: RegExp;
  try {
    re = new RegExp(pattern, flags);
  } catch (e) {
    console.error('[iframe-highlight] regex construction failed', { pattern, flags, e });
    return;
  }

  if (debug) console.log('[iframe-highlight] regex:', re.toString());

  let replacedNodes = 0;
  let totalMarksCreated = 0;

  (roots.filter(Boolean) as Element[]).forEach((root) => {
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const textNode = node as Text;
        const parent = textNode.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;

        if (ignoreTags.includes(parent.tagName)) return NodeFilter.FILTER_REJECT;

        const value = textNode.nodeValue ?? '';
        if (!value.trim()) return NodeFilter.FILTER_REJECT;

        if (parent.closest(`mark.${highlightClassName}`)) return NodeFilter.FILTER_REJECT;

        re.lastIndex = 0;
        if (!re.test(value)) return NodeFilter.FILTER_REJECT;

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes: Text[] = [];
    let n: Node | null;
    while ((n = walker.nextNode())) nodes.push(n as Text);

    if (debug) console.log('[iframe-highlight] accepted text nodes:', nodes.length, nodes);

    for (const textNode of nodes) {
      const text = textNode.nodeValue ?? '';
      re.lastIndex = 0;
      const matches = [...text.matchAll(re)];
      if (!matches.length) continue;

      const frag = root.ownerDocument.createDocumentFragment();
      let lastIndex = 0;

      for (const match of matches) {
        const index = match.index ?? 0;
        const mText = match[0];

        if (index > lastIndex) {
          frag.appendChild(root.ownerDocument.createTextNode(text.slice(lastIndex, index)));
        }

        const mark = root.ownerDocument.createElement('mark');
        mark.className = highlightClassName;
        mark.textContent = mText;
        frag.appendChild(mark);
        totalMarksCreated++;

        lastIndex = index + mText.length;
      }

      if (lastIndex < text.length) {
        frag.appendChild(root.ownerDocument.createTextNode(text.slice(lastIndex)));
      }

      textNode.parentNode?.replaceChild(frag, textNode);
      replacedNodes++;
    }
  });

  if (debug) {
    console.log('[iframe-highlight] done', { replacedNodes, totalMarksCreated });
  }
}

export function highlightInIframe({
  iframe,
  query,
  wholeWord = false,
  caseSensitive = false,
  ignoreTags,
  highlightClassName = 'dom-highlight',
  debug = false,
  rootSelector,
}: IframeHighlightOptions) {
  if (debug) console.log('[iframe-highlight] highlightInIframe called', { query, rootSelector });

  let doc: Document | null = null;
  try {
    doc = getIframeDocument(iframe);
  } catch (e) {
    console.error('[iframe-highlight] cannot access iframe document (likely cross-origin)', e);
    return;
  }

  if (!doc) {
    if (debug) console.warn('[iframe-highlight] iframe document is null (not loaded yet?)');
    return;
  }

  const root = (rootSelector ? doc.querySelector(rootSelector) : doc.body) as HTMLElement | null;

  if (!root) {
    console.warn('[iframe-highlight] root not found in iframe', { rootSelector });
    return;
  }

  // IMPORTANT: use the iframe document’s nodes, not the parent page’s
  clearDomHighlights(root, highlightClassName, debug);
  highlightDom([root], query, { wholeWord, caseSensitive, ignoreTags, highlightClassName, debug });
}

export function isDefined<T>(value: T | undefined | null): value is T {
  return value != null;
}
